import * as THREE from 'three';
import { RoomEnvironment } from 'three/addons/environments/RoomEnvironment.js';
import { RoundedBoxGeometry } from 'three/addons/geometries/RoundedBoxGeometry.js';
import { createCar, updateCarVisual } from './carFactory.js';
import { GaragePreview } from './garagePreview.js';
import {
  chaseCameraAimDirection,
  chaseCameraVerticalProfile,
  stabilizedChaseHeading,
} from './chaseCamera.js';
import { createMap, getGroundHeight } from './maps.js';
import { ArcadeVehicle, resolveLocalVehicleContacts, resolveVehicleContacts, normalizeAngle } from './physics.js';
import { EffectsSystem, ProjectileSystem } from './effects.js';
import {
  projectileDirectionToPoint,
  projectileInterceptTime,
  projectileLaunchState,
  projectileObstacleIntersection,
  projectileVehicleIntersection,
} from './projectileCombat.js';
import {
  WEAPON_AIM_MAX_PITCH_DOWN,
  WEAPON_AIM_MAX_PITCH_UP,
  WEAPON_AIM_MAX_YAW,
  clampWeaponAimDirection,
  shapeWeaponAimStick,
  slewWeaponAimDirection,
  weaponAimAngles,
  weaponAimDirection,
} from './weaponAim.js';
import { InputTelemetry } from './inputTelemetry.js';
import {
  CARS,
  COSMETIC_SLOTS,
  GAME_MODE_RULES,
  STANDARD_CANNON_PERFORMANCE,
  getBotDifficulty,
  getCannon,
  getCar,
  getDeathExplosion,
  getProjectile,
  getTombstone,
} from './data.js';
import { createBotAppearanceDeck } from './botAppearance.js';
import { GridNavigator } from './navigation.js';
import { RuptureArena } from './ruptureMode.js';
import { RupturePowerupDirector } from './rupturePowerups.js';
import {
  BotTelemetry,
  applyBotTrafficAvoidance,
  botAirControls,
  botObstacleClearance,
} from './botDriving.js';
import {
  NETWORK_FIXED_DT,
  NETWORK_INTERPOLATION_DELAY_MS,
  NETWORK_SNAPSHOT_RATE,
  applyNetworkCollider,
  applyNetworkVehicleState,
  extrapolateNetworkState,
  interpolateNetworkState,
  normalizeNetworkControls,
} from './networkSimulation.js';
import {
  POWERUP_DEFINITIONS,
  POWERUP_HOMING,
  POWERUP_NITRO,
  POWERUP_SHIELD,
  POWERUP_SHOCKWAVE,
  POWERUP_SHOCKWAVE_RADIUS,
  applyShockwaveImpulse,
  activateHeldPowerup,
  collectPowerup,
  createPowerupCrates,
  powerupTarget,
  randomPowerup,
  resetVehiclePowerups,
  updatePowerupCrates,
} from './powerups.js';

const tempA = new THREE.Vector3();
const tempB = new THREE.Vector3();
const tempC = new THREE.Vector3();
const tempD = new THREE.Vector3();
const tempE = new THREE.Vector3();
const CAMERA_UP = new THREE.Vector3(0,1,0);
const tempQuaternionA = new THREE.Quaternion();
const tempQuaternionB = new THREE.Quaternion();
const POST_MATCH_DURATION = 2.8;
const CAMERA_DISTANCE_PRESETS = [
  {
    id:'far',
    label:'LEJANA',
    distance:3.25,
    speedDistance:.68,
    airDistance:.65,
    height:2.18,
    speedHeight:.2,
  },
  {
    id:'medium',
    label:'INTERMEDIA',
    distance:2.7,
    speedDistance:.56,
    airDistance:.52,
    height:1.88,
    speedHeight:.17,
  },
  {
    id:'close',
    label:'CERCA',
    distance:2.15,
    speedDistance:.44,
    airDistance:.4,
    height:1.58,
    speedHeight:.14,
  },
];
const TUTORIAL_STEPS = Object.freeze([
  {
    id:'accelerate',
    title:'Despegá de la línea',
    instruction:'Acelerá y mantené el auto en movimiento.',
    keyboard:'W',
    gamepad:'RT',
    hold:.9,
  },
  {
    id:'steer',
    title:'Marcá una curva',
    instruction:'Mientras avanzás, doblá con decisión hacia cualquiera de los lados.',
    keyboard:'A / D',
    gamepad:'LS',
    hold:.7,
  },
  {
    id:'reverse',
    title:'Frená y retrocedé',
    instruction:'Usá el freno hasta detenerte y comenzar la marcha atrás.',
    keyboard:'S',
    gamepad:'LT',
    hold:.75,
  },
  {
    id:'handbrake',
    title:'Derrape de juguete',
    instruction:'Tomá velocidad, doblá y mantené el freno de mano para derrapar.',
    keyboard:'X',
    gamepad:'X',
    hold:.55,
  },
  {
    id:'jump',
    title:'¡Ruedas al aire!',
    instruction:'Saltá y volvé a tocar salto dentro de 0,8 s junto con una dirección para hacer un salto evasivo.',
    keyboard:'ESPACIO ×2 + WASD',
    gamepad:'A ×2 + LS',
    hold:0,
  },
  {
    id:'boost',
    title:'Encendé el nitro',
    instruction:'Activá el nitro en tierra o en el aire: Shift con teclado o B con mando.',
    keyboard:'SHIFT',
    gamepad:'B',
    hold:.65,
  },
  {
    id:'air-control',
    title:'Dominá el vuelo',
    instruction:'En el aire, W/S controla el cabeceo y A/D orienta el auto. Mantené C + A/D para tumbarlo. Con mando, usá LT + LS lateral.',
    keyboard:'WASD · C + A/D',
    gamepad:'LS · LT + LS',
    hold:.45,
  },
  {
    id:'stabilize',
    title:'Volvé sobre las ruedas',
    instruction:'Cuando las ruedas no apoyen, mantené estabilizar para enderezar el auto.',
    keyboard:'Q',
    gamepad:'L3',
    hold:.55,
  },
  {
    id:'fire',
    title:'Probá tu cañón',
    instruction:'Mové la cámara; el cañón acompaña sólo horizontalmente. Dispará con LMB o cualquiera de los bumpers.',
    keyboard:'RMB · LMB',
    gamepad:'RS · LB/RB',
    hold:0,
  },
]);

function setShadowRecursive(object) {
  object.traverse((child) => {
    if (child.isMesh) {
      child.castShadow = !child.userData.noShadow;
      child.receiveShadow = true;
    }
  });
}

function createVehicleHealthBar(teamColor = null) {
  const group=new THREE.Group();
  group.name='vehicle-health-bar';
  const frame=new THREE.Mesh(
    new THREE.PlaneGeometry(.94,.18),
    new THREE.MeshBasicMaterial({color:teamColor||'#282234',transparent:true,opacity:.94,depthTest:false,depthWrite:false}),
  );
  const background=new THREE.Mesh(
    new THREE.PlaneGeometry(.86,.12),
    new THREE.MeshBasicMaterial({color:'#171321',transparent:true,opacity:.82,depthTest:false,depthWrite:false}),
  );
  const fill=new THREE.Mesh(
    new THREE.PlaneGeometry(.78,.065),
    new THREE.MeshBasicMaterial({color:'#66ef8c',transparent:true,opacity:.96,depthTest:false,depthWrite:false}),
  );
  fill.position.z=.006;
  frame.position.z=-.006;
  frame.renderOrder=29;background.renderOrder=30;fill.renderOrder=31;
  group.add(frame,background,fill);
  group.userData.fill=fill;
  return group;
}

function createTeamRing(vehicle,color) {
  const ring=new THREE.Mesh(
    new THREE.RingGeometry(vehicle.car.userData.radius*.88,vehicle.car.userData.radius*1.08,28),
    new THREE.MeshBasicMaterial({color,transparent:true,opacity:.72,side:THREE.DoubleSide,depthWrite:false}),
  );
  ring.name='vehicle-team-ring';
  ring.rotation.x=-Math.PI/2;
  ring.renderOrder=8;
  ring.userData.vehicle=vehicle;
  return ring;
}

function createChaseMarker() {
  const group=new THREE.Group();
  group.name='chase-target-marker';
  const ring=new THREE.Mesh(
    new THREE.TorusGeometry(.34,.055,10,28),
    new THREE.MeshBasicMaterial({color:'#fff15e',depthTest:false,depthWrite:false}),
  );
  ring.rotation.x=Math.PI/2;
  ring.renderOrder=40;
  const arrow=new THREE.Mesh(
    new THREE.ConeGeometry(.16,.3,8),
    new THREE.MeshBasicMaterial({color:'#ff8b32',depthTest:false,depthWrite:false}),
  );
  arrow.rotation.x=Math.PI;
  arrow.position.y=-.29;
  arrow.renderOrder=40;
  group.add(ring,arrow);
  group.userData.ring=ring;
  return group;
}

function pointInsideObstacle(point, obstacles, margin = 1.3) {
  return obstacles.some((obstacle) => {
    if (obstacle.shape === 'circle') {
      const dx = point.x - obstacle.x;
      const dz = point.z - obstacle.z;
      const radius = obstacle.radius + margin;
      return dx * dx + dz * dz < radius * radius;
    }
    return point.x > obstacle.minX-margin && point.x < obstacle.maxX+margin
      && point.z > obstacle.minZ-margin && point.z < obstacle.maxZ+margin;
  });
}

function rayObstacleDistance(origin, direction, obstacle, maxDistance, margin = 0) {
  if (obstacle.shape === 'circle') {
    const dx = origin.x - obstacle.x;
    const dz = origin.z - obstacle.z;
    const radius = obstacle.radius + margin;
    const projection = dx * direction.x + dz * direction.z;
    const discriminant = projection * projection - (dx * dx + dz * dz - radius * radius);
    if (discriminant < 0) return null;
    const near = -projection - Math.sqrt(discriminant);
    const far = -projection + Math.sqrt(discriminant);
    const distance = near >= 0 ? near : far >= 0 ? 0 : null;
    return distance !== null && distance <= maxDistance ? distance : null;
  }

  let near = 0;
  let far = maxDistance;
  for (const axis of ['x','z']) {
    const minimum = obstacle[`min${axis.toUpperCase()}`] - margin;
    const maximum = obstacle[`max${axis.toUpperCase()}`] + margin;
    const originValue = origin[axis];
    const directionValue = direction[axis];
    if (Math.abs(directionValue) < .00001) {
      if (originValue < minimum || originValue > maximum) return null;
      continue;
    }
    let first = (minimum - originValue) / directionValue;
    let second = (maximum - originValue) / directionValue;
    if (first > second) [first,second] = [second,first];
    near = Math.max(near,first);
    far = Math.min(far,second);
    if (near > far) return null;
  }
  return near <= maxDistance ? Math.max(0,near) : null;
}

class BotBrain {
  constructor(vehicle,world,seed,navigator,difficulty='basic'){
    this.vehicle=vehicle;
    this.world=world;
    this.seed=seed;
    this.navigator=navigator;
    this.difficulty=getBotDifficulty(difficulty);
    this.target=new THREE.Vector3();
    this.plannedGoal=new THREE.Vector3(Infinity,0,Infinity);
    this.route=[];
    this.routeIndex=0;
    this.routeTimer=0;
    this.behavior='roam';
    this.behaviorClock=seed*.47;
    this.timer=0;
    this.stuckTimer=0;
    this.progressTimer=0;
    this.lastPosition=vehicle.car.position.clone();
    this.recoveryTimer=0;
    this.recoveryAttempts=0;
    this.recoverySteer=seed%2?1:-1;
    this.avoidanceTimer=0;
    this.avoidanceSide=this.recoverySteer;
    this.fireClock=Math.random();
    this.jumpCooldown=.4+seed*.16;
    this.engageTimer=0;
    this.combatTarget=null;
    this.combatRetargetClock=0;
    this.telemetry=new BotTelemetry();
    this.randomState=(seed+1)*9173;
    this.pickTarget();
  }

  random(){
    this.randomState=(this.randomState*1664525+1013904223)>>>0;
    return this.randomState/4294967296;
  }

  setTarget(point,behavior,replanDistance=3.5){
    this.target.copy(point);
    this.target.y=0;
    this.target.x=THREE.MathUtils.clamp(this.target.x,-this.world.width/2+1.6,this.world.width/2-1.6);
    this.target.z=THREE.MathUtils.clamp(this.target.z,-this.world.depth/2+1.6,this.world.depth/2-1.6);
    if(this.behavior!==behavior||this.plannedGoal.distanceToSquared(this.target)>replanDistance*replanDistance)this.routeTimer=0;
    this.behavior=behavior;
  }

  pickTarget(){
    const point=this.navigator.findRandomPoint(()=>this.random())||this.world.spawn.clone();
    this.setTarget(point,'roam',0);
    this.timer=6+this.random()*5;
  }

  pickEscapeTarget(vehicles){
    const position=this.vehicle.car.position;
    const escape=new THREE.Vector3();
    for(const other of vehicles){
      if(other===this.vehicle)continue;
      const away=new THREE.Vector3().subVectors(position,other.car.position);away.y=0;
      const distance=Math.max(.6,away.length());
      escape.addScaledVector(away.normalize(),1/distance);
    }
    const forward=new THREE.Vector3(-Math.sin(this.vehicle.yaw),0,-Math.cos(this.vehicle.yaw));
    if(escape.lengthSq()<.01)escape.copy(forward);
    else escape.normalize().addScaledVector(forward,.55).normalize();
    const edgeX=Math.max(0,Math.abs(position.x)-(this.world.width/2-12));
    const edgeZ=Math.max(0,Math.abs(position.z)-(this.world.depth/2-12));
    escape.x-=Math.sign(position.x)*edgeX*.12;
    escape.z-=Math.sign(position.z)*edgeZ*.12;
    escape.normalize();

    let best=null;
    let bestScore=-Infinity;
    const baseAngle=Math.atan2(escape.z,escape.x);
    for(const offset of [0,.35,-.35,.72,-.72,1.12,-1.12,Math.PI]){
      const angle=baseAngle+offset+(this.random()-.5)*.12;
      const radius=12+this.random()*8;
      const candidate=position.clone().add(new THREE.Vector3(Math.cos(angle)*radius,0,Math.sin(angle)*radius));
      candidate.x=THREE.MathUtils.clamp(candidate.x,-this.world.width/2+2,this.world.width/2-2);
      candidate.z=THREE.MathUtils.clamp(candidate.z,-this.world.depth/2+2,this.world.depth/2-2);
      const safeCandidate=this.navigator.nearestWalkablePoint(candidate);
      if(!safeCandidate||safeCandidate.distanceToSquared(position)<36)continue;
      let nearest=Infinity;
      for(const other of vehicles){
        if(other!==this.vehicle)nearest=Math.min(nearest,safeCandidate.distanceTo(other.car.position));
      }
      const openBonus=this.navigator.hasLineOfSight(position,safeCandidate)?5:0;
      const score=nearest*2+position.distanceTo(safeCandidate)*.18+openBonus-Math.abs(offset)*1.4;
      if(score>bestScore){bestScore=score;best=safeCandidate;}
    }
    if(best)this.setTarget(best,'flee',0);else this.pickTarget();
    this.timer=1.15+this.random()*.65;
  }

  updateRoute(){
    const position=this.vehicle.car.position;
    let route=[];
    if(this.navigator.hasLineOfSight(position,this.target))route=[this.target.clone()];
    else route=this.navigator.findPath(position,this.target);
    if(!route.length){
      const fallback=this.navigator.nearestWalkablePoint(this.target);
      route=[fallback||this.target.clone()];
    }
    this.route=route;
    this.routeIndex=0;
    this.plannedGoal.copy(this.target);
    this.routeTimer=(this.behavior==='pursue' ? .48+this.random()*.18
      :this.behavior==='engage' ? .62+this.random()*.2
        :this.behavior==='flee' ? .7+this.random()*.2
          :1.2+this.random()*.45)*this.difficulty.routeRefreshScale;
  }

  navigationTarget(){
    const position=this.vehicle.car.position;
    while(this.routeIndex<this.route.length-1&&position.distanceToSquared(this.route[this.routeIndex])<5.3)this.routeIndex+=1;
    if(this.routeIndex<this.route.length-1&&this.navigator.hasLineOfSight(position,this.route[this.routeIndex+1]))this.routeIndex+=1;
    return this.route[this.routeIndex]||this.target;
  }

  rescueFromStuck(navigationPoint){
    const position=this.vehicle.car.position;
    const distance=position.distanceTo(navigationPoint);
    if(distance<.5)return false;
    const candidate=position.clone().lerp(navigationPoint,Math.min(1,4.2/distance));
    const safe=this.navigator.nearestWalkablePoint(candidate);
    if(!safe||safe.distanceToSquared(position)<1)return false;
    const yaw=Math.atan2(-(this.target.x-safe.x),-(this.target.z-safe.z));
    this.vehicle.reset(safe,yaw);
    this.lastPosition.copy(safe);
    this.stuckTimer=0;
    this.recoveryTimer=0;
    this.recoveryAttempts=0;
    this.routeTimer=0;
    this.telemetry.recordRecovery(true);
    return true;
  }

  update(dt,player,vehicles,modeState=null){
    this.timer-=dt;
    this.routeTimer-=dt;
    this.fireClock-=dt;
    this.combatRetargetClock-=dt;
    this.jumpCooldown=Math.max(0,this.jumpCooldown-dt);
    this.behaviorClock+=dt;
    const position=this.vehicle.car.position;
    if(this.vehicle.grounded&&!this.vehicle.wheelsGrounded){
      const recovery=this.vehicle.airRecoveryError();
      const jump=this.jumpCooldown<=0;
      if(jump)this.jumpCooldown=.58+this.random()*.2;
      this.telemetry.update(dt,this.vehicle,{stuck:true,target:null});
      return {
        throttle:0,
        steer:0,
        jump,
        boost:false,
        handbrake:false,
        fire:false,
        airPitch:THREE.MathUtils.clamp(recovery.pitch*1.45,-1,1),
        airYaw:0,
        airRoll:THREE.MathUtils.clamp(recovery.roll*1.45,-1,1),
      };
    }
    const isChase=modeState?.type==='chase'&&Boolean(modeState.pursued);
    const isBattle=modeState?.type==='battle';
    const isFreeForAll=modeState?.type==='ffa';
    const isCombat=isBattle||isFreeForAll;
    const aiRules=GAME_MODE_RULES[isChase?'chase':isBattle?'battle':isFreeForAll?'ffa':'practice'];
    if(isCombat){
      const targetStillValid=this.combatTarget&&!this.combatTarget.disabled
        &&(isFreeForAll||this.combatTarget.teamId!==this.vehicle.teamId);
      if(!targetStillValid||this.combatRetargetClock<=0){
        this.combatTarget=vehicles
          .filter((other)=>other!==this.vehicle&&!other.disabled&&(isFreeForAll||other.teamId!==this.vehicle.teamId))
          .map((candidate)=>({
            candidate,
            cost:position.distanceTo(candidate.car.position)
              +vehicles.filter((other)=>other!==this.vehicle&&other.aiTarget===candidate
                &&(isFreeForAll||other.teamId===this.vehicle.teamId)).length*(isFreeForAll?12:9),
          }))
          .sort((a,b)=>a.cost-b.cost)[0]?.candidate||null;
        this.vehicle.aiTarget=this.combatTarget;
        this.combatRetargetClock=(.85+this.random()*.55)*this.difficulty.routeRefreshScale;
      }
    }else{
      this.combatTarget=null;
      this.vehicle.aiTarget=null;
    }
    const isPursued=isChase&&modeState.pursued===this.vehicle;
    const chaseCombatTarget=isChase
      ?isPursued
        ?vehicles.filter((other)=>other!==this.vehicle&&!other.disabled)
          .sort((a,b)=>position.distanceToSquared(a.car.position)-position.distanceToSquared(b.car.position))[0]||player
        :modeState.pursued
      :null;
    const combatTarget=isCombat?(this.combatTarget||player):chaseCombatTarget||player;
    const targetDistance=position.distanceTo(combatTarget.car.position);
    let pursuedDistance=Infinity;

    if(isPursued){
      if(position.distanceToSquared(this.target)<8||this.timer<=0)this.pickEscapeTarget(vehicles);
    }else if(isChase){
      const pursued=modeState.pursued;
      pursuedDistance=position.distanceTo(pursued.car.position);
      const leadTime=THREE.MathUtils.clamp(pursuedDistance/(this.vehicle.config.maxSpeed+10),.18,.78);
      const intercept=pursued.car.position.clone().addScaledVector(pursued.velocity,leadTime);
      if(pursuedDistance>6){
        const slotAngle=this.seed/Math.max(1,vehicles.length-1)*Math.PI*2;
        const slotRadius=THREE.MathUtils.clamp(pursuedDistance*.055,.75,2.2);
        intercept.x+=Math.cos(slotAngle)*slotRadius;
        intercept.z+=Math.sin(slotAngle)*slotRadius;
      }
      this.setTarget(intercept,'pursue',3.2);
    }else{
      if(targetDistance<(isCombat?34:25))this.engageTimer=3.5;
      else this.engageTimer=Math.max(0,this.engageTimer-dt);
      if(this.engageTimer>0){
        const orbitAngle=this.seed/Math.max(1,vehicles.length-1)*Math.PI*2+Math.sin(this.behaviorClock*.32+this.seed)*.42;
        const orbitRadius=isCombat?(targetDistance>11?2.2+(this.seed%3)*.45:3.6+(this.seed%3)*.6):4.7+(this.seed%3)*1.15;
        const engagePoint=combatTarget.car.position.clone().addScaledVector(combatTarget.velocity,.24);
        engagePoint.x+=Math.cos(orbitAngle)*orbitRadius;
        engagePoint.z+=Math.sin(orbitAngle)*orbitRadius;
        this.setTarget(engagePoint,'engage',3.8);
      }else if(position.distanceToSquared(this.target)<9||this.timer<=0)this.pickTarget();
    }

    if(this.routeTimer<=0||!this.route.length)this.updateRoute();
    const navigationPoint=this.navigationTarget();
    tempA.subVectors(navigationPoint,position);tempA.y=0;
    const targetVehicle=isChase&&!isPursued?modeState.pursued:isCombat?combatTarget:null;
    const {traffic}=applyBotTrafficAvoidance(tempA,this.vehicle,vehicles,{
      target:targetVehicle,
      strength:isPursued?1.12:.76,
    });

    const desiredYaw=Math.atan2(-tempA.x,-tempA.z);
    const delta=normalizeAngle(desiredYaw-this.vehicle.yaw);
    const aimPoint=combatTarget.car.position.clone().addScaledVector(
      combatTarget.velocity,
      this.difficulty.aimLead,
    );
    const playerDelta=tempB.subVectors(aimPoint,position);
    const aimLength=Math.max(.0001,playerDelta.length());
    const aimX=playerDelta.x/aimLength;
    const aimY=playerDelta.y/aimLength;
    const aimZ=playerDelta.z/aimLength;
    const playerYaw=Math.atan2(-playerDelta.x,-playerDelta.z);
    const aimDelta=normalizeAngle(playerYaw-this.vehicle.yaw);
    const hasTargetSight=this.navigator.hasLineOfSight(position,combatTarget.car.position);
    const canCheckShot=targetDistance<aiRules.aiShotDistance
      &&Math.abs(aimDelta)<aiRules.aiShotAngle*this.difficulty.aimAngleScale
      &&this.fireClock<=0;
    const fire=canCheckShot&&hasTargetSight;
    if(fire){
      this.fireClock=(aiRules.aiFireDelay+this.random()*aiRules.aiFireJitter)
        *this.difficulty.fireDelayScale;
    }

    this.progressTimer+=dt;
    if(this.progressTimer>=.62){
      const progress=position.distanceTo(this.lastPosition);
      const wantsMovement=position.distanceToSquared(navigationPoint)>7;
      if(wantsMovement&&progress<.38&&this.vehicle.grounded)this.stuckTimer+=this.progressTimer;
      else this.stuckTimer=Math.max(0,this.stuckTimer-this.progressTimer*1.8);
      if(progress>1.05)this.recoveryAttempts=Math.max(0,this.recoveryAttempts-1);
      this.lastPosition.copy(position);
      this.progressTimer=0;
    }

    if(this.stuckTimer>1.3*this.difficulty.routeRefreshScale&&this.recoveryTimer<=0){
      const left=botObstacleClearance(position,this.vehicle.yaw+.82,this.world,5,.72);
      const right=botObstacleClearance(position,this.vehicle.yaw-.82,this.world,5,.72);
      this.recoverySteer=right>=left?1:-1;
      this.recoveryTimer=.72+this.random()*.32;
      this.recoveryAttempts+=1;
      this.telemetry.recordRecovery(false);
      this.stuckTimer=.38;
      this.routeTimer=0;
      if(this.recoveryAttempts>=3&&this.rescueFromStuck(navigationPoint))return {throttle:0,steer:0,jump:false,boost:false,handbrake:false,fire:false};
    }

    if(this.recoveryTimer>0){
      this.recoveryTimer-=dt;
      this.telemetry.update(dt,this.vehicle,{stuck:true,target:combatTarget});
      return {throttle:-1,steer:this.recoverySteer,jump:false,boost:false,handbrake:false,fire:false,airYaw:0,airPitch:0,airRoll:0};
    }

    const frontClearance=botObstacleClearance(position,this.vehicle.yaw,this.world,6,.72);
    const leftClearance=botObstacleClearance(position,this.vehicle.yaw+.72,this.world,5.2,.72);
    const rightClearance=botObstacleClearance(position,this.vehicle.yaw-.72,this.world,5.2,.72);
    this.avoidanceTimer=Math.max(0,this.avoidanceTimer-dt);
    if(frontClearance<3.7&&this.avoidanceTimer<=0){
      this.avoidanceSide=rightClearance>=leftClearance?1:-1;
      this.avoidanceTimer=.58+this.random()*.42;
    }
    const avoidance=this.avoidanceTimer>0?this.avoidanceSide:(rightClearance>=leftClearance?1:-1);
    const avoidanceWeight=THREE.MathUtils.clamp((3-frontClearance)/2.4,0,.82);
    const steeringDelta=isCombat&&targetDistance<20&&hasTargetSight
      ?normalizeAngle(delta+normalizeAngle(aimDelta-delta)*.82):delta;
    const targetSteer=THREE.MathUtils.clamp(
      -steeringDelta*this.difficulty.steeringGain,
      -1,
      1,
    );
    let steer=THREE.MathUtils.lerp(targetSteer,avoidance,avoidanceWeight);
    if(traffic>.15)steer=THREE.MathUtils.clamp(steer+this.recoverySteer*traffic*.42,-1,1);
    const reversing=Math.abs(delta)>2.5&&Math.abs(this.vehicle.forwardSpeed)<1.4;
    if(reversing)steer*=-1;
    const turnThrottle=Math.abs(delta)>1.25 ? .42 : Math.abs(delta)>.72 ? .68 : 1;
    const obstacleThrottle=frontClearance<.72 ? -.22 : frontClearance<1.45 ? .28 : 1;
    const throttle=reversing?-1:Math.min(turnThrottle,obstacleThrottle,1-traffic*.62);

    let jump=false;
    if(this.jumpCooldown<=0&&frontClearance<1.05&&frontClearance>.2&&Math.abs(delta)<.48&&Math.abs(this.vehicle.forwardSpeed)>2.8){
      jump=true;
      this.jumpCooldown=2.1+this.random();
    }
    const boost=!this.vehicle.grounded&&throttle>.12;
    const handbrake=Math.abs(delta)>.78&&Math.abs(this.vehicle.forwardSpeed)>7.2;
    const speedScale=isChase
      ?isPursued?aiRules.pursuedSpeedScale
        :aiRules.chaserSpeedScale+THREE.MathUtils.clamp(pursuedDistance/45,0,aiRules.chaserDistanceBonus)
      :1;
    const accelerationScale=isChase&&!isPursued?aiRules.chaserAccelerationScale:1;
    const air=botAirControls(this.vehicle,steer,boost);
    this.telemetry.update(dt,this.vehicle,{
      stuck:this.stuckTimer>.45,
      target:combatTarget,
    });
    return {
      throttle,
      steer,
      jump,
      boost,
      handbrake,
      fire,
      aimX,
      aimY,
      aimZ,
      ...air,
      speedScale,
      accelerationScale,
    };
  }
}

export class ToyboxGame {
  constructor(canvas, input, audio, callbacks = {}) {
    this.canvas = canvas;
    this.input = input;
    this.audio = audio;
    this.callbacks = callbacks;
    this.mode = 'menu';
    this.gameType = 'practice';
    this.selectedCarId = 'muscle';
    this.selectedLoadout = this.defaultLoadout(this.selectedCarId);
    this.selectedMapId = 'living';
    this.botDifficulty = 'basic';
    this.world = null;
    this.navigator = null;
    this.showcaseCar = null;
    this.player = null;
    this.vehicles = [];
    this.bots = [];
    this.onlineSession = null;
    this.onlinePaused = false;
    this.onlineVehicles = new Map();
    this.powerupCrates=[];
    this.rupturePowerupDirector=null;
    this.powerupCrateVisuals=new Map();
    this.homingTarget=null;
    this.onlineNetworkStats = {rtt:null,jitter:0,loss:0,quality:'offline'};
    this.healthBars = [];
    this.teamRings = [];
    this.clock = new THREE.Clock();
    this.elapsed = 0;
    this.gameElapsed = 0;
    this.startCountdownRemaining = 0;
    this.startCountdownLabel = null;
    this.cameraShake = 0;
    this.cameraShakeStrength = 1;
    this.nitroCameraBlend = 0;
    this.cameraDistanceIndex = 0;
    this.dustClock = 0;
    this.boostClock = 0;
    this.chaseDuration = GAME_MODE_RULES.chase.duration;
    this.chaseRemaining = this.chaseDuration;
    this.chasePursued = null;
    this.chaseScores = new Map();
    this.chaseTransferCooldown = 0;
    this.chaseLastPair = null;
    this.chaseMarker = null;
    this.battleDuration = GAME_MODE_RULES.battle.duration;
    this.battleRemaining = this.battleDuration;
    this.battleKillLimit = GAME_MODE_RULES.battle.killLimit;
    this.battleScore = { blue:0, red:0 };
    this.battleWinner = null;
    this.ffaDuration = GAME_MODE_RULES.ffa.duration;
    this.ffaRemaining = this.ffaDuration;
    this.ffaKillLimit = GAME_MODE_RULES.ffa.killLimit;
    this.ffaWinner = null;
    this.ffaOvertime = false;
    this.ffaOvertimeContenders = new Set();
    this.ruptureArena = null;
    this.ruptureWinner = null;
    this.ruptureFinishTimer = 0;
    this.tutorial = null;
    this.postMatchTimer = 0;
    this.postMatchResult = null;
    this.trailerDirector = null;
    this.cinematicStudio = null;
    this.graphicsSettings={
      renderScale:100,
      shadowQuality:'high',
      particleQuality:'high',
      fpsLimit:60,
      showFps:false,
    };
    this.lastRenderedAt=0;
    this.fpsWindowStartedAt=performance.now();
    this.fpsWindowFrames=0;

    this.renderer = new THREE.WebGLRenderer({ canvas, antialias: true, powerPreference: 'high-performance' });
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 1.75));
    this.renderer.setSize(window.innerWidth, window.innerHeight, false);
    this.renderer.outputColorSpace = THREE.SRGBColorSpace;
    this.renderer.toneMapping = THREE.ACESFilmicToneMapping;
    this.renderer.toneMappingExposure = .98;
    this.renderer.shadowMap.enabled = true;
    this.renderer.shadowMap.type = THREE.PCFSoftShadowMap;

    this.scene = new THREE.Scene();
    this.scene.background = new THREE.Color('#b99376');
    this.scene.fog = new THREE.Fog('#b99376', 30, 66);
    const environmentGenerator = new THREE.PMREMGenerator(this.renderer);
    this.scene.environment = environmentGenerator.fromScene(new RoomEnvironment(), .04).texture;
    environmentGenerator.dispose();
    this.camera = new THREE.PerspectiveCamera(60, window.innerWidth/window.innerHeight, .08, 280);
    this.camera.position.set(7,5,14);
    this.cameraTarget = new THREE.Vector3();
    this.cameraPosition = new THREE.Vector3();
    this.cameraOrbitYaw = 0;
    this.cameraHeading = new THREE.Vector3(0,0,-1);
    this.cameraFollowedVehicle = null;
    this.combatAimDirection = new THREE.Vector3(0,0,-1);
    this.combatCameraAimDirection = new THREE.Vector3(0,0,-1);
    this.combatAimYaw = 0;
    this.combatAimPitch = 0;
    this.combatCameraAimYaw = 0;
    this.combatCameraAimPitch = 0;
    this.combatAimIdleTime = 0;
    this.combatPrecisionAim = false;
    this.combatLookX = 0;
    this.combatLookY = 0;
    this.combatAimInput = {x:0,y:0};
    this.combatCameraReverse = false;
    this.combatAimTarget = null;
    this.combatAimAssistTarget = null;
    this.aimSettings={
      controllerSensitivity:.5,
      controllerAimAssist:true,
      controllerAimAssistStrength:.45,
    };
    this.matchInputDevice=null;
    this.inputTelemetry=new InputTelemetry();
    this.effects = new EffectsSystem(this.scene);
    this.projectiles = new ProjectileSystem(
      this.scene,
      this.effects,
      (position,target,owner,projectileId,details)=>this.handleProjectileImpact(
        position,
        owner,
        projectileId,
        target,
        details,
      ),
      (projectile)=>this.playNetworkProjectileShot(projectile),
    );
    this.garagePreview=new GaragePreview(this.camera,this.input,this.scene.environment);
    this.garagePreviewElement=null;
    this.setupLights();
    this.audio.playMenuMusic();
    this.setMap(this.selectedMapId);
    this.setCar(this.selectedCarId);
    window.addEventListener('resize',()=>this.resize());
  }

  setupLights() {
    const hemisphere = new THREE.HemisphereLight(0xfff2dc,0x5e577a,1.7);
    this.scene.add(hemisphere);
    this.sun = new THREE.DirectionalLight(0xfff0d2,3);
    this.sun.position.set(32,55,36);
    this.sun.castShadow=true;
    this.sun.shadow.mapSize.set(2048,2048);
    this.sun.shadow.camera.left=-56;this.sun.shadow.camera.right=56;this.sun.shadow.camera.top=56;this.sun.shadow.camera.bottom=-56;
    this.sun.shadow.camera.near=.5;this.sun.shadow.camera.far=170;this.sun.shadow.bias=-.0001;this.sun.shadow.normalBias=.025;
    this.sunOffset=new THREE.Vector3(32,55,36);
    this.scene.add(this.sun,this.sun.target);
    const fill = new THREE.DirectionalLight(0x879cff,.75);fill.position.set(-15,8,-8);this.scene.add(fill);
  }

  setGraphicsSettings(value={}){
    const renderScale=THREE.MathUtils.clamp(Number(value.renderScale)||100,50,125);
    const shadowQuality=['off','low','high'].includes(value.shadowQuality)
      ?value.shadowQuality
      :'high';
    const particleQuality=['low','medium','high'].includes(value.particleQuality)
      ?value.particleQuality
      :'high';
    const fpsLimit=[0,30,60,120].includes(Number(value.fpsLimit))
      ?Number(value.fpsLimit)
      :60;
    this.graphicsSettings={
      renderScale,
      shadowQuality,
      particleQuality,
      fpsLimit,
      showFps:value.showFps===true,
    };
    const shadowMapSize=shadowQuality==='high'?2048:1024;
    this.renderer.shadowMap.enabled=shadowQuality!=='off';
    this.renderer.shadowMap.type=shadowQuality==='high'
      ?THREE.PCFSoftShadowMap
      :THREE.PCFShadowMap;
    [this.scene,this.garagePreview?.scene].filter(Boolean).forEach((scene)=>{
      scene.traverse((object)=>{
        if(!object.isDirectionalLight||!object.shadow)return;
        if(!object.castShadow&&!object.userData.graphicsShadowSource)return;
        object.userData.graphicsShadowSource=true;
        object.castShadow=shadowQuality!=='off';
        if(object.shadow.mapSize.x===shadowMapSize)return;
        object.shadow.mapSize.set(shadowMapSize,shadowMapSize);
        object.shadow.map?.dispose?.();
        object.shadow.map=null;
      });
    });
    this.renderer.shadowMap.needsUpdate=true;
    this.effects?.setQuality(particleQuality);
    this.garagePreview?.effects?.setQuality(particleQuality);
    this.world?.setGraphicsQuality?.(particleQuality);
    this.lastRenderedAt=0;
    this.resize();
  }

  graphicsPixelRatio(){
    const scale=(this.graphicsSettings?.renderScale||100)/100;
    return Math.min(window.devicePixelRatio*scale,1.75);
  }

  shouldRenderFrame(now){
    if(this.mode==='trailer'||this.mode==='cinematic')return true;
    const limit=this.graphicsSettings?.fpsLimit||0;
    if(!limit||!this.lastRenderedAt){
      this.lastRenderedAt=now;
      return true;
    }
    const interval=1000/limit;
    const elapsed=now-this.lastRenderedAt;
    if(elapsed+0.35<interval)return false;
    this.lastRenderedAt=now-(elapsed%interval);
    return true;
  }

  recordRenderedFrame(now){
    this.fpsWindowFrames+=1;
    const elapsed=now-this.fpsWindowStartedAt;
    if(elapsed<500)return;
    const fps=Math.round(this.fpsWindowFrames*1000/Math.max(1,elapsed));
    this.fpsWindowFrames=0;
    this.fpsWindowStartedAt=now;
    this.callbacks.onFps?.(fps);
  }

  defaultLoadout(id) {
    const car = getCar(id);
    return {
      wheelId:car.defaultWheelId,
      engineSoundId:car.defaultEngineSoundId,
      cannonId:car.defaultCannonId,
      projectileId:car.defaultProjectileId,
      nitroId:car.defaultNitroId,
      explosionId:car.defaultExplosionId,
      tombstoneId:car.defaultTombstoneId,
    };
  }

  setCar(id, loadout = null) {
    const carChanged = this.selectedCarId !== id;
    this.selectedCarId=id;
    if (typeof loadout === 'string') loadout = { wheelId:loadout };
    if (loadout) this.selectedLoadout = { ...this.defaultLoadout(id), ...loadout };
    else if (carChanged || !this.selectedLoadout) this.selectedLoadout = this.defaultLoadout(id);
    if(this.mode!=='menu')return;
    if(this.showcaseCar)this.scene.remove(this.showcaseCar);
    // El selector funciona como una vitrina: el auto se presenta más grande,
    // pero conserva su escala diminuta cuando entra al mapa.
    this.showcaseCar=createCar(id,{scale:1.68,loadout:this.selectedLoadout});
    this.showcaseCar.position.copy(this.world.showcase || this.world.spawn);
    this.showcaseCar.position.y += .04;
    this.showcaseCar.rotation.y=this.world.showcaseYaw ?? this.world.spawnYaw;
    setShadowRecursive(this.showcaseCar);
    this.scene.add(this.showcaseCar);
  }

  setWheel(wheelId) {
    this.setLoadout({ ...this.selectedLoadout, wheelId });
  }

  setLoadout(loadout) {
    this.selectedLoadout = { ...this.defaultLoadout(this.selectedCarId), ...loadout };
    if (this.mode === 'menu') this.setCar(this.selectedCarId, this.selectedLoadout);
  }

  setBotDifficulty(difficultyId='basic'){
    this.botDifficulty=getBotDifficulty(difficultyId).id;
  }

  setMap(id) {
    this.selectedMapId=id;
    if(this.world)this.scene.remove(this.world.group);
    this.world=createMap(id);
    this.world.setGraphicsQuality?.(this.graphicsSettings?.particleQuality||'high');
    setShadowRecursive(this.world.group);
    this.scene.add(this.world.group);
    const palettes={
      living:['#c39b7d','#c39b7d'],
      bedroom:['#82aabd','#82aabd'],
      garden:['#8ecad0','#8ecad0'],
      'rupture-void':['#171331','#2d2357'],
      'rupture-storm':['#102b32','#173c46'],
    };
    const [background,fog]=palettes[id]||palettes.living;
    const isRuptureMap=id.startsWith('rupture-');
    this.scene.background.set(background);this.scene.fog.color.set(fog);
    this.scene.fog.near=isRuptureMap?95:id==='garden'?112:92;
    this.scene.fog.far=isRuptureMap?220:id==='garden'?225:185;
    this.sun.intensity=id==='garden'?3.5:isRuptureMap?3.1:2.8;
    this.configureMapLighting();
    if(this.mode==='menu'){
      this.setCar(this.selectedCarId);
      this.snapMenuCamera();
    }
  }

  openGaragePreview(carId,loadout,slot){
    this.mode='garage';
    this.input.clear();
    this.garagePreview.show(carId,loadout,slot);
  }

  setGaragePreviewElement(element){
    this.garagePreviewElement=element;
  }

  updateGaragePreview(carId,loadout,slot){
    if(this.mode!=='garage')return;
    this.garagePreview.setSelection(carId,loadout,slot);
  }

  closeGaragePreview(){
    this.garagePreview.hide();
    this.mode='menu';
    this.setCar(this.selectedCarId,this.selectedLoadout);
    this.snapMenuCamera();
    this.clock.getDelta();
  }

  orbitGarageCamera(deltaX,deltaY){
    this.garagePreview.orbit(deltaX,deltaY);
  }

  zoomGarageCamera(delta){
    this.garagePreview.zoom(delta);
  }

  resetGarageCamera(){
    this.garagePreview.resetCamera();
  }

  onlineInterpolationDelayFor(
    network=this.onlineNetworkStats,
    snapshotJitter=0,
    mode=this.onlineSession?.match?.mode,
  ){
    const rtt=network?.rtt===null||network?.rtt===undefined?0:Math.max(0,Number(network.rtt)||0);
    const jitter=Math.max(Math.max(0,Number(network?.jitter)||0),Math.max(0,snapshotJitter||0));
    const loss=Math.max(0,Number(network?.loss)||0);
    // En Ruptura los autos vuelan y cambian de dirección mucho más rápido.
    // Un buffer de 100 ms los dibuja sensiblemente detrás de su posición
    // autoritativa; un snapshot (50 ms) más un pequeño margen mantiene la
    // suavidad sin volverlos blancos atrasados para choques y proyectiles.
    const baseDelay=mode==='rupture'?60:NETWORK_INTERPOLATION_DELAY_MS;
    return THREE.MathUtils.clamp(
      baseDelay+Math.max(0,rtt-140)*.12+jitter*1.45+loss*1.1,
      baseDelay,
      mode==='rupture'?150:190,
    );
  }

  setOnlineNetworkStats(network={}){
    this.onlineNetworkStats={...this.onlineNetworkStats,...network};
    if(!this.onlineSession)return;
    this.onlineSession.interpolationTargetMs=this.onlineInterpolationDelayFor(
      this.onlineNetworkStats,
      this.onlineSession.snapshotJitterMs,
    );
  }

  createOnlineCollisionProxy(vehicle,networkId){
    const proxyCar=new THREE.Object3D();
    proxyCar.userData={
      config:vehicle.config,
      radius:vehicle.car.userData.radius,
      length:vehicle.car.userData.length,
      wheelRadius:vehicle.car.userData.wheelRadius,
      collisionHeight:vehicle.car.userData.collisionHeight||.82,
      collisionWidth:vehicle.car.userData.collisionWidth,
      collisionLength:vehicle.car.userData.collisionLength,
      collisionOffsetX:vehicle.car.userData.collisionOffsetX||0,
      collisionOffsetY:vehicle.car.userData.collisionOffsetY,
      collisionOffsetZ:vehicle.car.userData.collisionOffsetZ||0,
    };
    const proxy=new ArcadeVehicle(proxyCar,this.world,{
      position:vehicle.car.position.clone(),
      yaw:vehicle.yaw,
    });
    proxy.networkId=networkId;
    return proxy;
  }

  startPractice() {
    this.startMatch('practice');
  }

  startTutorial() {
    this.startMatch('tutorial');
  }

  startChase() {
    this.startMatch('chase');
  }

  startBattle() {
    this.startMatch('battle');
  }

  startFreeForAll() {
    this.startMatch('ffa');
  }

  startRupture() {
    if(!this.selectedMapId.startsWith('rupture-'))this.setMap('rupture-void');
    this.startMatch('rupture');
  }

  startOnlineMatch(match,playerId,sendInput){
    this.inputTelemetry.finish(false);
    this.input.endMatchInput();
    this.clearVehicles();
    this.input.clear();
    this.mode='online-game';
    this.postMatchTimer=0;
    this.postMatchResult=null;
    this.onlinePaused=false;
    this.gameType='online';
    this.gameElapsed=0;
    this.nitroCameraBlend=0;
    this.cameraOrbitYaw=0;
    this.showcaseCar=null;
    if(this.selectedMapId!==match.mapId)this.setMap(match.mapId);
    this.onlineVehicles=new Map();
    const modeRules={...(GAME_MODE_RULES[match.mode]||GAME_MODE_RULES.ffa),...(match.rules||{})};
    if(match.mode==='rupture'){
      this.ruptureArena=new RuptureArena(this.scene,this.world,modeRules,{mapId:match.mapId});
    }

    match.players.forEach((playerData,index)=>{
      const car=createCar(playerData.carId,{loadout:playerData.loadout});
      applyNetworkCollider(car,playerData.carId,playerData.collider);
      setShadowRecursive(car);
      this.scene.add(car);
      const spawn=playerData.spawn||{x:0,y:.02,z:0,yaw:0};
      const local=playerData.id===playerId;
      const vehicle=new ArcadeVehicle(car,this.world,{
        position:new THREE.Vector3(spawn.x,spawn.y,spawn.z),
        yaw:spawn.yaw,
        isPlayer:local,
        onImpact:local?(force,position)=>this.handleImpact(force,position):undefined,
        onLand:local?(airTime,force)=>this.handleLanding(airTime,force):undefined,
        onJump:local?()=>this.audio.blip(260,.12,'sine',.065):undefined,
        onDodge:local?(dodge)=>this.handleAirDodge(dodge):undefined,
      });
      vehicle.displayName=local?'VOS':playerData.name;
      vehicle.networkId=playerData.id;
      vehicle.teamId=playerData.teamId;
      vehicle.gameMode=match.mode;
      vehicle.combatStats={kills:0,deaths:0,assists:0};
      vehicle.respawnDelay=modeRules.respawnDelay||3;
      vehicle.respawnProtection=modeRules.respawnProtection||0;
      if(match.mode==='rupture'){
        vehicle.ruptureSlot=index;
        vehicle.ruptureFuses=modeRules.fuses;
        vehicle.ruptureInstability=0;
        vehicle.ruptureRespawnTimer=0;
        vehicle.ruptureEliminated=false;
        vehicle.ruptureReachedPlatform=0;
        vehicle.getRespawn=()=>{
          const ruptureSpawn=this.ruptureArena.spawnFor(vehicle.ruptureSlot);
          return {position:ruptureSpawn.position,yaw:ruptureSpawn.yaw};
        };
      }
      let presentation=null;
      if(local){
        presentation=new THREE.Group();
        presentation.name='network-local-presentation';
        car.add(presentation);
        presentation.add(car.userData.visual);
        car.userData.networkPresentation=presentation;
      }
      const collisionProxy=local?null:this.createOnlineCollisionProxy(vehicle,playerData.id);
      this.vehicles.push(vehicle);
      this.onlineVehicles.set(playerData.id,{
        vehicle,
        playerData,
        presentation,
        collisionProxy,
        samples:[],
      });
      if(local)this.player=vehicle;
      else{
        if(playerData.teamId)this.attachTeamRing(vehicle,playerData.teamId==='blue'?'#49dff5':'#ff5d75');
        if(match.mode==='ffa'||match.mode==='battle'||match.mode==='rupture')this.attachHealthBar(
          vehicle,
          playerData.teamId?(playerData.teamId==='blue'?'#49dff5':'#ff5d75'):null,
        );
      }
    });

    if(!this.player){
      this.backToMenu();
      return false;
    }
    this.matchInputDevice=this.input.beginMatchInput();
    this.inputTelemetry.begin(this.matchInputDevice,match.mode||'online');
    this.resetCombatAim();
    if(match.mode==='chase'){
      this.chaseMarker=createChaseMarker();
      this.chaseMarker.visible=false;
      this.scene.add(this.chaseMarker);
    }
    this.onlineSession={
      match,
      playerId,
      sendInput,
      sequence:0,
      accumulator:0,
      pendingInputs:[],
      queuedJump:false,
      queuedFireCount:0,
      queuedPowerup:false,
      heldControls:normalizeNetworkControls(),
      lastSnapshotTick:-1,
      lastSnapshotAt:performance.now(),
      startCountdownDeadline:performance.now()+Math.max(0,Number(match.startsInMs)||0),
      snapshotIntervalMs:1000/NETWORK_SNAPSHOT_RATE,
      snapshotJitterMs:0,
      serverTimelineOffsetMs:null,
      interpolationDelayMs:this.onlineInterpolationDelayFor(
        this.onlineNetworkStats,
        0,
        match.mode,
      ),
      interpolationTargetMs:this.onlineInterpolationDelayFor(
        this.onlineNetworkStats,
        0,
        match.mode,
      ),
      visualPositionOffset:new THREE.Vector3(),
      visualRotationOffset:new THREE.Quaternion(),
      identityRotation:new THREE.Quaternion(),
      lastCorrectionDistance:0,
      previousPredictedPosition:this.player.car.position.clone(),
      previousPredictedRotation:this.player.car.quaternion.clone(),
      renderPosition:this.player.car.position.clone(),
      renderRotation:this.player.car.quaternion.clone(),
      matchState:null,
      lastEventId:0,
      finished:false,
      localAuthoritativeDisabled:false,
      spectatorTargetId:null,
    };
    this.camera.position.copy(this.player.car.position).add(new THREE.Vector3(0,3,5.4));
    this.cameraTarget.copy(this.player.car.position);
    this.effects.clear();
    this.projectiles.clear();
    this.audio.ensure();
    this.vehicles.forEach((vehicle)=>{
      this.preloadVehicleAudio(vehicle);
    });
    this.audio.pauseMusic(false);
    this.audio.playRandomLevelMusic();
    this.callbacks.onStart?.(this.getOnlineMatchState());
    this.beginStartCountdown((Number(match.startsInMs)||0)/1000);
    return true;
  }

  applyOnlineSnapshot(snapshot){
    const session=this.onlineSession;
    if(this.mode!=='online-game'||!session||snapshot.matchId!==session.match.id||snapshot.tick<=session.lastSnapshotTick)return;
    const receivedAt=performance.now();
    if(session.lastSnapshotTick>=0){
      const arrivalInterval=receivedAt-session.lastSnapshotAt;
      const previousInterval=session.snapshotIntervalMs;
      session.snapshotIntervalMs=THREE.MathUtils.lerp(previousInterval,arrivalInterval,.15);
      session.snapshotJitterMs=THREE.MathUtils.lerp(
        session.snapshotJitterMs,
        Math.abs(arrivalInterval-previousInterval),
        .15,
      );
      session.interpolationTargetMs=this.onlineInterpolationDelayFor(
        this.onlineNetworkStats,
        session.snapshotJitterMs,
      );
    }
    session.lastSnapshotTick=snapshot.tick;
    session.lastSnapshotAt=receivedAt;
    const authoritativeMatchState=snapshot.matchState||session.matchState;
    session.matchState=authoritativeMatchState;
    if(authoritativeMatchState?.powerups){
      this.powerupCrates=authoritativeMatchState.powerups;
      this.syncPowerupCrateVisuals(this.powerupCrates);
    }
    if(session.match.mode==='rupture'&&authoritativeMatchState){
      // La reconciliación local debe repetirse contra exactamente las mismas
      // baldosas que usó el servidor para este snapshot. Aplicar este estado
      // después del replay hacía que el cliente predijera apoyo o choques
      // contra piezas que el servidor ya había retirado.
      this.ruptureArena?.applyAuthoritativeState(authoritativeMatchState);
    }
    let sampleAt=receivedAt;
    if(Number.isFinite(snapshot.serverTime)){
      const measuredOffset=receivedAt-snapshot.serverTime;
      if(session.serverTimelineOffsetMs===null)session.serverTimelineOffsetMs=measuredOffset;
      else{
        // Conserva la cadencia estable del reloj del servidor aunque los
        // paquetes individuales lleguen antes o después por jitter de red.
        // Se adapta rápido a una ruta más corta y muy lento a picos tardíos.
        const offsetAlpha=measuredOffset<session.serverTimelineOffsetMs ? .08 : .002;
        session.serverTimelineOffsetMs=THREE.MathUtils.lerp(
          session.serverTimelineOffsetMs,
          measuredOffset,
          offsetAlpha,
        );
      }
      sampleAt=snapshot.serverTime+session.serverTimelineOffsetMs;
    }
    let localAck=-1;
    const orderedStates=[...snapshot.players].sort((first,second)=>(first.id===session.playerId)-(second.id===session.playerId));
    orderedStates.forEach((state)=>{
      const entry=this.onlineVehicles.get(state.id);
      if(!entry)return;
      if(state.id!==session.playerId){
        const previousState=entry.samples.at(-1)?.state;
        const discontinuity=previousState&&(
          Boolean(previousState.disabled)!==Boolean(state.disabled)
          ||(
            session.match.mode==='rupture'
            &&previousState.ruptureReachedPlatform!==state.ruptureReachedPlatform
            &&Math.hypot(
              state.x-previousState.x,
              state.y-previousState.y,
              state.z-previousState.z,
            )>4
          )
        );
        if(discontinuity)entry.samples.length=0;
        entry.samples.push({receivedAt:sampleAt,state});
        if(entry.samples.length>10)entry.samples.splice(0,entry.samples.length-10);
        return;
      }
      this.updateOnlineRemoteCollisionProxies();
      localAck=Number.isSafeInteger(state.ack)?state.ack:localAck;
      session.pendingInputs=session.pendingInputs.filter((input)=>input.seq>state.ack);
      const callbacks={
        onImpact:entry.vehicle.onImpact,
        onLand:entry.vehicle.onLand,
        onJump:entry.vehicle.onJump,
        onDodge:entry.vehicle.onDodge,
      };
      entry.vehicle.onImpact=()=>{};
      entry.vehicle.onLand=()=>{};
      entry.vehicle.onJump=()=>{};
      entry.vehicle.onDodge=()=>{};
      const visiblePosition=session.renderPosition.clone();
      const visibleRotation=session.renderRotation.clone();
      const previousPositionDelta=session.previousPredictedPosition.clone().sub(entry.vehicle.car.position);
      const previousRotationDelta=entry.vehicle.car.quaternion.clone().invert()
        .multiply(session.previousPredictedRotation);
      applyNetworkVehicleState(entry.vehicle,state);
      const wasAuthoritativelyDisabled=session.localAuthoritativeDisabled;
      session.localAuthoritativeDisabled=entry.vehicle.disabled;
      if(wasAuthoritativelyDisabled||entry.vehicle.disabled){
        // El servidor es el único que puede reaparecer un auto online. En
        // especial, `respawnTimer:null` representa un eliminado definitivo y
        // nunca debe disparar ArcadeVehicle.reset() durante la predicción.
        session.pendingInputs.length=0;
        session.previousPredictedPosition.copy(entry.vehicle.car.position);
        session.previousPredictedRotation.copy(entry.vehicle.car.quaternion);
        session.renderPosition.copy(entry.vehicle.car.position);
        session.renderRotation.copy(entry.vehicle.car.quaternion);
        session.visualPositionOffset.set(0,0,0);
        session.visualRotationOffset.identity();
        if(entry.presentation){
          entry.presentation.position.set(0,0,0);
          entry.presentation.quaternion.identity();
        }
        return;
      }
      session.pendingInputs.forEach((input)=>{
        const previous=entry.vehicle.car.position.clone();
        entry.vehicle.update(NETWORK_FIXED_DT,input.controls,false);
        this.resolveOnlineLocalContacts(entry.vehicle,previous);
      });
      Object.assign(entry.vehicle,callbacks);
      session.previousPredictedPosition.copy(entry.vehicle.car.position).add(previousPositionDelta);
      session.previousPredictedRotation.copy(entry.vehicle.car.quaternion)
        .multiply(previousRotationDelta).normalize();
      const renderAlpha=THREE.MathUtils.clamp(session.accumulator/NETWORK_FIXED_DT,0,1);
      const interpolatedPosition=session.previousPredictedPosition.clone()
        .lerp(entry.vehicle.car.position,renderAlpha);
      const interpolatedRotation=session.previousPredictedRotation.clone()
        .slerp(entry.vehicle.car.quaternion,renderAlpha).normalize();
      session.visualPositionOffset.copy(visiblePosition).sub(interpolatedPosition);
      session.visualRotationOffset.copy(interpolatedRotation).invert()
        .multiply(visibleRotation).normalize();
      session.lastCorrectionDistance=session.visualPositionOffset.length();
      if(session.lastCorrectionDistance>2.25){
        session.visualPositionOffset.set(0,0,0);
        session.visualRotationOffset.identity();
        session.renderPosition.copy(interpolatedPosition);
        session.renderRotation.copy(interpolatedRotation);
      }
    });
    this.projectiles.syncNetwork(
      snapshot.projectiles||[],
      (ownerId)=>this.onlineVehicles.get(ownerId)?.vehicle||null,
      {localOwnerId:session.playerId,localAck},
    );
    this.handleOnlineEvents(snapshot.events||[]);
    if(session.matchState?.finished&&!session.finished){
      session.finished=true;
      const result=this.getOnlineMatchState();
      this.beginPostMatch(result);
    }
  }

  handleOnlineEvents(events){
    const session=this.onlineSession;
    if(!session)return;
    events.forEach((event)=>{
      if(!Number.isFinite(event.id)||event.id<=session.lastEventId)return;
      session.lastEventId=event.id;
      const position=new THREE.Vector3(event.position?.x||0,event.position?.y||0,event.position?.z||0);
      if(event.type==='powerup-pickup'){
        this.effects.powerupCrateOpen(position);
        if(event.playerId===session.playerId)this.audio.blip(540,.12,'triangle',.08);
        return;
      }
      if(event.type==='powerup-activate'){
        const definition=POWERUP_DEFINITIONS[event.powerup];
        this.effects.burst(position,definition?.color||'#ffffff',14,4.8);
        if(event.playerId===session.playerId)this.audio.blip(680,.16,'square',.09);
        return;
      }
      if(event.type==='powerup-shockwave'){
        this.effects.shockwave(position.clone().add(new THREE.Vector3(0,.18,0)),{
          shockwaveColor:'#cf8cff',shockwaveSize:POWERUP_SHOCKWAVE_RADIUS/1.4,
        });
        const distance=this.player?.car.position.distanceTo(position)||0;
        if(distance<POWERUP_SHOCKWAVE_RADIUS*1.25){
          this.cameraShake=Math.max(this.cameraShake,.52*(1-distance/(POWERUP_SHOCKWAVE_RADIUS*1.4)));
        }
        if(event.ownerId===session.playerId)this.input.rumble({
          duration:420,weakMagnitude:.72,strongMagnitude:.95,priority:4,key:'powerup-shockwave',
        });
        return;
      }
      if(event.type==='chase-tag'){
        const previousEntry=this.onlineVehicles.get(event.previousId);
        const pursuedEntry=this.onlineVehicles.get(event.pursuedId);
        this.effects.burst(position,event.initial?'#fff15e':'#ff9c43',event.initial?14:20,event.initial?4:6);
        if(!event.initial){
          this.audio.blip(event.pursuedId===session.playerId?175:420,.16,'square',.09);
          if(event.previousId===session.playerId||event.pursuedId===session.playerId){
            this.cameraShake=Math.max(this.cameraShake,.34);
          }
          this.callbacks.onChaseTag?.({
            previousName:previousEntry?.vehicle.displayName||'—',
            pursuedName:pursuedEntry?.vehicle.displayName||'AUTO',
            pursuedIsPlayer:event.pursuedId===session.playerId,
          });
        }
        return;
      }
      if(event.type==='chase-overload'){
        this.effects.burst(position,'#74efff',18,6.2);
        this.audio.blip(165,.2,'sawtooth',.1);
        if(event.targetId===session.playerId)this.cameraShake=Math.max(this.cameraShake,.42);
        return;
      }
      if(event.type==='rupture-reveal'){
        this.effects.burst(position,'#fff16a',22,7);
        this.audio.blip(520,.22,'square',.1);
        this.callbacks.onRuptureEvent?.({type:'reveal'});
        return;
      }
      if(event.type==='rupture-collapse'){
        this.cameraShake=Math.max(this.cameraShake,.34);
        this.audio.blip(92,.36,'sawtooth',.12);
        this.callbacks.onRuptureEvent?.({type:'collapse'});
        return;
      }
      if(event.type==='rupture-stage'){
        this.callbacks.onRuptureEvent?.({
          type:'stage',
          platform:(event.platformIndex||0)+1,
        });
        return;
      }
      if(event.type==='rupture-final-warning'){
        this.audio.blip(185,.42,'square',.12);
        this.callbacks.onRuptureEvent?.({type:'final-warning'});
        return;
      }
      if(event.type==='rupture-final-ring'){
        this.cameraShake=Math.max(this.cameraShake,.28);
        this.audio.blip(72+(event.ringsRemaining||0)*16,.24,'sawtooth',.11);
        return;
      }
      if(event.type==='rupture-landed'){
        if(event.playerId===session.playerId){
          this.effects.burst(position,'#8ff7ff',10,3.2);
          this.callbacks.onRuptureEvent?.({
            type:'landed',
            platform:(event.platformIndex||0)+1,
          });
        }
        return;
      }
      if(event.type==='rupture-fall'){
        const fallen=this.onlineVehicles.get(event.playerId);
        if(fallen)this.playRuptureFallEffect(fallen.vehicle,position);
        this.callbacks.onRuptureEvent?.({
          type:event.eliminated?'eliminated':'fuse',
          name:fallen?.vehicle.displayName||'UN PILOTO',
          isPlayer:event.playerId===session.playerId,
          fuses:event.fuses||0,
        });
        return;
      }
      if(event.type.startsWith('rupture-'))return;
      if(event.type==='projectile-impact'){
        this.projectiles.removePredicted(event.ownerId,event.sourceSeq);
        this.effects.burst(position,event.absorbed?'#68f1ff':event.targetId?'#ffdf55':'#ffd9a0',event.absorbed?12:event.targetId?9:5,event.absorbed?5.2:event.targetId?4.5:2.8);
        const ownerEntry=event.ownerId===session.playerId
          ?{vehicle:this.player}
          :this.onlineVehicles.get(event.ownerId);
        const targetEntry=event.targetId===session.playerId
          ?{vehicle:this.player}
          :this.onlineVehicles.get(event.targetId);
        if(!event.absorbed&&event.ownerId===session.playerId&&event.targetId&&event.targetId!==session.playerId){
          this.inputTelemetry.hit();
        }
        this.handleProjectileImpact(
          position,
          ownerEntry?.vehicle||null,
          event.projectileId,
          targetEntry?.vehicle||null,
          {absorbed:Boolean(event.absorbed)},
        );
        if(event.targetId===session.playerId)this.cameraShake=Math.max(this.cameraShake,event.absorbed?.07:.24);
        return;
      }
      if(event.type!=='elimination')return;
      const victimEntry=this.onlineVehicles.get(event.victimId);
      const killerEntry=this.onlineVehicles.get(event.killerId);
      if(event.killerId===session.playerId)this.inputTelemetry.elimination();
      if(victimEntry)this.playVehicleDestroyedEffect(victimEntry.vehicle,position);
      this.callbacks.onBattleEvent?.({
        victimName:victimEntry?.vehicle.displayName||'UN PILOTO',
        victimIsPlayer:event.victimId===session.playerId,
        killerName:killerEntry?.vehicle.displayName||'EL ESCENARIO',
        killerIsPlayer:event.killerId===session.playerId,
        playerAssist:event.assistIds?.includes(session.playerId)||false,
      });
    });
  }

  updateOnlineLocalPresentation(dt){
    const session=this.onlineSession;
    const presentation=this.onlineVehicles.get(session?.playerId)?.presentation;
    if(!session||!presentation||!this.player)return;
    const car=this.player.car;
    const correctionDistance=session.visualPositionOffset.length();
    const correctionStrength=correctionDistance>.9?9:6;
    const alpha=1-Math.exp(-correctionStrength*dt);
    session.visualPositionOffset.multiplyScalar(Math.exp(-correctionStrength*dt));
    session.visualRotationOffset.slerp(session.identityRotation,alpha).normalize();
    const renderAlpha=THREE.MathUtils.clamp(session.accumulator/NETWORK_FIXED_DT,0,1);
    session.renderPosition.copy(session.previousPredictedPosition)
      .lerp(car.position,renderAlpha)
      .add(session.visualPositionOffset);
    if(this.player.grounded){
      // La continuidad visual no debe dibujar el auto por debajo de un piso
      // que la simulación local ya reconoce como apoyo.
      session.renderPosition.y=Math.max(session.renderPosition.y,car.position.y);
    }
    session.renderRotation.copy(session.previousPredictedRotation)
      .slerp(car.quaternion,renderAlpha)
      .multiply(session.visualRotationOffset).normalize();
    presentation.position.copy(session.renderPosition).sub(car.position)
      .applyQuaternion(tempQuaternionA.copy(car.quaternion).invert())
      .divide(car.scale);
    presentation.quaternion.copy(tempQuaternionB.copy(car.quaternion).invert())
      .multiply(session.renderRotation).normalize();
  }

  updateOnlineRemoteVehicles(dt){
    const session=this.onlineSession;
    if(!session)return;
    session.interpolationDelayMs=THREE.MathUtils.lerp(
      session.interpolationDelayMs,
      session.interpolationTargetMs,
      1-Math.exp(-2.2*dt),
    );
    const renderAt=performance.now()-session.interpolationDelayMs;
    this.onlineVehicles.forEach((entry,id)=>{
      if(id===session.playerId||!entry.samples.length)return;
      while(entry.samples.length>=3&&entry.samples[1].receivedAt<=renderAt)entry.samples.shift();
      const latest=entry.samples.at(-1);
      let state=entry.samples[0].state;
      if(renderAt>=latest.receivedAt){
        const previous=entry.samples.at(-2)||latest;
        const sampleSeconds=Math.max(.001,(latest.receivedAt-previous.receivedAt)/1000);
        state=extrapolateNetworkState(
          previous.state,
          latest.state,
          (renderAt-latest.receivedAt)/1000,
          sampleSeconds,
        );
      }else if(entry.samples.length>=2){
        const first=entry.samples[0];
        const second=entry.samples[1];
        const duration=Math.max(1,second.receivedAt-first.receivedAt);
        const alpha=THREE.MathUtils.clamp((renderAt-first.receivedAt)/duration,0,1);
        state=interpolateNetworkState(first.state,second.state,alpha);
      }
      entry.renderedState=state;
      entry.renderedServerTime=session.serverTimelineOffsetMs===null
        ?null
        :renderAt-session.serverTimelineOffsetMs;
      applyNetworkVehicleState(entry.vehicle,state);
      updateCarVisual(entry.vehicle.car,entry.vehicle,dt);
    });
  }

  updateOnlineRemoteCollisionProxies(){
    const session=this.onlineSession;
    if(!session)return;
    this.onlineVehicles.forEach((entry,id)=>{
      if(id===session.playerId||!entry.collisionProxy||!entry.renderedState)return;
      // El collider coincide con lo que el jugador realmente ve. Conservamos
      // por separado el instante del servidor representado para que éste pueda
      // rebobinar al mismo cuadro al validar el contacto.
      applyNetworkVehicleState(entry.collisionProxy,entry.renderedState);
      entry.collisionProxy.networkSampleTime=entry.renderedServerTime;
    });
  }

  resolveOnlineLocalContacts(vehicle,previousPosition){
    const session=this.onlineSession;
    if(!session||!vehicle||vehicle.disabled)return [];
    const remoteVehicles=[];
    this.onlineVehicles.forEach((entry,id)=>{
      const proxy=entry.collisionProxy;
      if(id!==session.playerId&&proxy&&!proxy.disabled)remoteVehicles.push(proxy);
    });
    const contacts=resolveLocalVehicleContacts(
      vehicle,
      remoteVehicles,
      previousPosition,
      NETWORK_FIXED_DT,
    );
    if(contacts.length)vehicle.resolveWorldCollisions(previousPosition);
    return contacts;
  }

  updateOnlineChasePresentation(){
    const session=this.onlineSession;
    const state=session?.matchState;
    if(!session||session.match.mode!=='chase'||!state||!this.chaseMarker)return;
    const pursuedEntry=this.onlineVehicles.get(state.pursuedId);
    if(!pursuedEntry){this.chaseMarker.visible=false;return;}
    this.chasePursued=pursuedEntry.vehicle;
    this.chaseMarker.visible=true;
    const position=state.pursuedId===session.playerId
      ?session.renderPosition:pursuedEntry.vehicle.car.position;
    this.chaseMarker.position.copy(position);
    this.chaseMarker.position.y+=pursuedEntry.vehicle.car.userData.collisionHeight+.72+Math.sin(this.gameElapsed*5)*.08;
    this.chaseMarker.rotation.y=this.gameElapsed*1.8;
    this.chaseMarker.userData.ring.scale.setScalar(1+Math.sin(this.gameElapsed*7)*.09);
  }

  getOnlineMatchState(){
    const session=this.onlineSession;
    const serverState=session?.matchState;
    const mode=session?.match.mode||'ffa';
    if(session&&mode==='rupture'){
      const rules={...GAME_MODE_RULES.rupture,...(session.match.rules||{})};
      const ruptureState=serverState||{
        phase:{
          id:'combat',
          label:'PELEÁ',
          remaining:rules.combatDuration,
          duration:rules.combatDuration,
        },
        platformIndex:0,
        platformCount:6,
        platformName:'PLATAFORMA INICIAL',
        nextPlatformIndex:null,
        nextPlatformName:null,
        final:false,
        scoreboard:session.match.players.map((player)=>({
          id:player.id,
          name:player.name,
          fuses:rules.fuses,
          instability:0,
          platform:1,
          eliminated:false,
          disabled:false,
        })),
        finished:false,
        winnerId:null,
      };
      const scoreboard=ruptureState.scoreboard.map((entry)=>({
        ...entry,
        isPlayer:entry.id===session.playerId,
        isWinner:entry.id===ruptureState.winnerId,
        color:this.onlineVehicles.get(entry.id)?.vehicle.config.color||'#ffffff',
      }));
      const playerState=scoreboard.find((entry)=>entry.isPlayer);
      let route=null;
      if(!playerState?.eliminated&&ruptureState.nextPlatformIndex!==null&&this.player){
        const destination=this.ruptureArena?.getStage(ruptureState.nextPlatformIndex);
        if(destination){
          const toDestination=destination.center.clone().sub(this.player.car.position);
          const horizontalDistance=Math.hypot(toDestination.x,toDestination.z);
          const cameraForward=new THREE.Vector3();
          this.camera.getWorldDirection(cameraForward);
          cameraForward.y=0;
          if(cameraForward.lengthSq()<.0001)cameraForward.set(0,0,-1);
          cameraForward.normalize();
          const cameraRight=new THREE.Vector3().crossVectors(cameraForward,CAMERA_UP).normalize();
          route={
            angle:Math.atan2(
              toDestination.dot(cameraRight),
              toDestination.x*cameraForward.x+toDestination.z*cameraForward.z,
            ),
            distance:horizontalDistance,
            rise:destination.top-this.player.car.position.y,
          };
        }
      }
      return {
        type:'rupture',
        online:true,
        onlineMode:'rupture',
        phase:ruptureState.phase,
        platformIndex:ruptureState.platformIndex,
        platformCount:ruptureState.platformCount,
        platformName:ruptureState.platformName,
        nextPlatformIndex:ruptureState.nextPlatformIndex,
        nextPlatformName:ruptureState.nextPlatformName,
        final:ruptureState.final,
        fuses:playerState?.fuses??this.player?.ruptureFuses??rules.fuses,
        instability:playerState?.instability??this.player?.ruptureInstability??0,
        protection:0,
        respawn:this.player?.disabled?(this.player.ruptureRespawnTimer||0):0,
        eliminated:Boolean(playerState?.eliminated),
        spectating:Boolean(playerState?.eliminated),
        spectatorName:playerState?.eliminated
          ?this.onlineVehicles.get(session.spectatorTargetId)?.vehicle?.displayName||null
          :null,
        survivors:scoreboard.filter((entry)=>!entry.eliminated).length,
        participantCount:scoreboard.length,
        route,
        scoreboard,
        winner:scoreboard.find((entry)=>entry.isWinner)
          ||(ruptureState.finished?scoreboard[0]:null),
        finished:ruptureState.finished,
        connected:this.startCountdownRemaining>0||performance.now()-session.lastSnapshotAt<1600,
      };
    }
    if(session&&mode==='chase'){
      const rules={...GAME_MODE_RULES.chase,...(session.match.rules||{})};
      const initialPursuedId=session.match.players[1]?.id||session.match.players[0]?.id;
      const chaseState=serverState||{
        remaining:rules.duration,
        duration:rules.duration,
        protection:rules.tagProtectionInitial,
        pursuedId:initialPursuedId,
        targetInstability:0,
        finished:false,
        winnerId:null,
        scoreboard:session.match.players.map((player)=>({id:player.id,name:player.name,seconds:0,isPursued:player.id===initialPursuedId})),
      };
      const scoreboard=chaseState.scoreboard.map((entry)=>({
        ...entry,
        isPlayer:entry.id===session.playerId,
        color:this.onlineVehicles.get(entry.id)?.vehicle.config.color||'#ffffff',
      }));
      const pursued=this.onlineVehicles.get(chaseState.pursuedId)?.vehicle;
      return {
        type:'chase',
        online:true,
        onlineMode:'chase',
        remaining:chaseState.remaining,
        duration:chaseState.duration,
        protection:chaseState.protection,
        pursuedName:pursued?.displayName||'—',
        pursuedIsPlayer:chaseState.pursuedId===session.playerId,
        targetInstability:(chaseState.targetInstability||0)/GAME_MODE_RULES.chase.instabilityThreshold*100,
        playerSlowed:(this.player?.chaseSlowTimer||0)>0,
        playerNitroLocked:(this.player?.chaseNitroLockTimer||0)>0,
        participantCount:scoreboard.length,
        scoreboard,
        finished:chaseState.finished,
        winner:scoreboard.find((entry)=>entry.id===chaseState.winnerId)||(chaseState.finished?scoreboard[0]:null),
        connected:this.startCountdownRemaining>0||performance.now()-session.lastSnapshotAt<1600,
      };
    }
    if(session&&(mode==='ffa'||mode==='battle')){
      const rules={...GAME_MODE_RULES[mode],...(session.match.rules||{})};
      const competitiveState=serverState||{
        scoreboard:session.match.players.map((player)=>({
          id:player.id,name:player.name,teamId:player.teamId,kills:0,deaths:0,assists:0,disabled:false,
        })),
        remaining:rules.duration,
        duration:rules.duration,
        killLimit:rules.killLimit,
        overtime:false,
        finished:false,
        winnerId:null,
        winnerTeam:null,
        blueScore:0,
        redScore:0,
      };
      const scoreboard=competitiveState.scoreboard.map((entry)=>({
        ...entry,
        isPlayer:entry.id===session.playerId,
        isWinner:entry.id===competitiveState.winnerId,
        color:this.onlineVehicles.get(entry.id)?.vehicle.config.color||'#ffffff',
      }));
      const playerStats=scoreboard.find((entry)=>entry.isPlayer);
      const common={
        type:mode,
        online:true,
        onlineMode:mode,
        remaining:competitiveState.remaining,
        duration:competitiveState.duration,
        killLimit:competitiveState.killLimit,
        overtime:competitiveState.overtime,
        respawn:this.player?.disabled?this.player.respawnTimer:0,
        protection:this.player?.invulnerableTimer||0,
        participantCount:scoreboard.length,
        playerStats,
        scoreboard,
        finished:competitiveState.finished,
        connected:this.startCountdownRemaining>0||performance.now()-session.lastSnapshotAt<1600,
      };
      if(mode==='battle')return {
        ...common,
        blueScore:competitiveState.blueScore,
        redScore:competitiveState.redScore,
        winnerTeam:competitiveState.winnerTeam,
        playerWon:competitiveState.winnerTeam===this.player.teamId,
        teams:{
          blue:scoreboard.filter((entry)=>entry.teamId==='blue'),
          red:scoreboard.filter((entry)=>entry.teamId==='red'),
        },
      };
      return {
        ...common,
        playerRank:1+scoreboard.filter((entry)=>entry.kills>(playerStats?.kills||0)).length,
        winner:scoreboard.find((entry)=>entry.isWinner)||(competitiveState.finished?scoreboard[0]:null),
      };
    }
    return {
      type:'online',
      online:true,
      onlineMode:mode,
      participantCount:session?.match.players.length||this.vehicles.length,
      connected:session?(this.startCountdownRemaining>0||performance.now()-session.lastSnapshotAt<1600):false,
    };
  }

  updateOnlineGame(dt){
    const session=this.onlineSession;
    if(!session||!this.player)return;
    if(session.match.mode==='rupture')this.ruptureArena?.updateVisual(dt);
    const frameControls=this.updateCombatAim(this.input.snapshot(),dt);
    if(frameControls.pause){
      this.pauseOnline(!this.onlinePaused);
      this.input.endFrame();
      return;
    }
    const countdownRemaining=session.startCountdownDeadline
      ?Math.max(0,(session.startCountdownDeadline-performance.now())/1000)
      :Math.max(0,this.startCountdownRemaining-dt);
    const countdownDt=Math.max(0,this.startCountdownRemaining-countdownRemaining);
    if(this.updateStartCountdown(countdownDt)){
      session.queuedJump=false;
      session.queuedFireCount=0;
      session.queuedPowerup=false;
      session.heldControls=normalizeNetworkControls();
      session.accumulator=0;
      this.updateOnlineRemoteVehicles(dt);
      updateCarVisual(this.player.car,this.player,dt);
      this.updateOnlineLocalPresentation(dt);
      this.updateOnlineChasePresentation();
      this.updateChaseCamera(dt);
      if(session.match.mode==='battle')this.updateTeamRings();
      if(session.match.mode==='battle'||session.match.mode==='ffa'||session.match.mode==='rupture'){
        this.updateHealthBars();
      }
      this.updatePlayerVehicleAudio();
      this.callbacks.onHud?.(this.player,this.getOnlineMatchState());
      this.input.endFrame();
      return;
    }
    this.gameElapsed+=dt;
    const activeControls=this.onlinePaused||this.player.disabled
      ?normalizeNetworkControls()
      :frameControls;
    session.queuedJump ||= activeControls.jump;
    session.queuedFireCount=Math.min(2,session.queuedFireCount+(activeControls.fire?1:0));
    session.queuedPowerup ||= activeControls.powerup;
    session.heldControls=normalizeNetworkControls({...activeControls,jump:false,fire:false,powerup:false});
    session.accumulator=Math.min(session.accumulator+dt,NETWORK_FIXED_DT*4);
    let steps=0;
    while(session.accumulator>=NETWORK_FIXED_DT&&steps<4){
      const controls={...session.heldControls,jump:session.queuedJump,
        fire:session.queuedFireCount>0,powerup:session.queuedPowerup};
      session.queuedJump=false;
      session.queuedFireCount=Math.max(0,session.queuedFireCount-1);
      session.queuedPowerup=false;
      const seq=++session.sequence;
      session.previousPredictedPosition.copy(this.player.car.position);
      session.previousPredictedRotation.copy(this.player.car.quaternion);
      if(session.match.mode==='chase')this.updateChaseCombatState(NETWORK_FIXED_DT);
      const predictedControls=this.applyChaseControlEffects(this.player,controls);
      if(controls.powerup&&!this.player.disabled)activateHeldPowerup(this.player);
      if(!this.player.disabled){
        this.player.update(NETWORK_FIXED_DT,predictedControls,false);
        this.applyVehicleAimControls(this.player,controls,NETWORK_FIXED_DT);
      }
      if(controls.fire&&!this.player.disabled&&this.projectiles.firePredicted(
        this.player,
        seq,
        session.playerId,
        this.player.aimDirection,
        {homingTarget:this.player.homingAmmoTimer>0?this.selectHomingTarget(this.player,this.homingTarget):null},
      ))this.playProjectileShot(this.player);
      this.updateOnlineRemoteCollisionProxies();
      const contacts=this.player.disabled
        ?[]:this.resolveOnlineLocalContacts(this.player,session.previousPredictedPosition);
      const contactClaims=contacts.slice(0,3).map((contact)=>({
        targetId:contact.b.networkId,
        contactTime:contact.b.networkSampleTime,
        actorX:this.player.car.position.x,
        actorY:this.player.car.position.y,
        actorZ:this.player.car.position.z,
        targetX:contact.b.car.position.x,
        targetY:contact.b.car.position.y,
        targetZ:contact.b.car.position.z,
      })).filter((claim)=>claim.targetId);
      const networkControls=normalizeNetworkControls({
        ...controls,
        // Se envía el objetivo del cañón, no la orientación ya interpolada.
        // Cliente y servidor ejecutan independientemente la misma velocidad
        // angular física y el estado autoritativo puede validarla sin doble
        // retraso cuando se agrupan paquetes.
        aimX:controls.aimX,
        aimY:controls.aimY,
        aimZ:controls.aimZ,
      });
      if(!this.player.disabled)session.pendingInputs.push({seq,controls:{
        ...predictedControls,
        aimX:networkControls.aimX,
        aimY:networkControls.aimY,
        aimZ:networkControls.aimZ,
      }});
      if(session.pendingInputs.length>90)session.pendingInputs.shift();
      session.sendInput(seq,networkControls,{
        contacts:contactClaims,
        remoteViewDelayMs:session.interpolationDelayMs,
      });
      session.accumulator-=NETWORK_FIXED_DT;
      steps+=1;
    }
    this.updateOnlineRemoteVehicles(dt);
    this.homingTarget=this.player.homingAmmoTimer>0
      ?this.selectHomingTarget(this.player,this.homingTarget):null;
    this.projectiles.updateNetwork(dt);
    this.updatePowerupVisuals(dt);
    updateCarVisual(this.player.car,this.player,dt);
    this.updateOnlineLocalPresentation(dt);
    this.updateOnlineChasePresentation();
    this.updateEffectsForPlayer(dt);
    this.effects.update(dt);
    this.updateChaseCamera(dt);
    if(session.match.mode==='battle')this.updateTeamRings();
    if(session.match.mode==='battle'||session.match.mode==='ffa'||session.match.mode==='rupture'){
      this.updateHealthBars();
    }
    this.updatePlayerVehicleAudio();
    this.callbacks.onHud?.(this.player,this.getOnlineMatchState());
    this.input.endFrame();
  }

  startMatch(gameType='practice') {
    this.inputTelemetry.finish(false);
    this.input.endMatchInput();
    this.clearVehicles();
    this.input.clear();
    this.mode='game';
    this.matchInputDevice=this.input.beginMatchInput();
    this.inputTelemetry.begin(this.matchInputDevice,gameType);
    this.postMatchTimer=0;
    this.postMatchResult=null;
    this.gameType=gameType;
    this.gameElapsed=0;
    this.nitroCameraBlend=0;
    this.cameraOrbitYaw=0;
    this.chaseRemaining=this.chaseDuration;
    this.chasePursued=null;
    this.chaseScores=new Map();
    this.chaseTransferCooldown=0;
    this.chaseLastPair=null;
    this.battleRemaining=this.battleDuration;
    this.battleScore={blue:0,red:0};
    this.battleWinner=null;
    this.ffaRemaining=this.ffaDuration;
    this.ffaWinner=null;
    this.ffaOvertime=false;
    this.ffaOvertimeContenders=new Set();
    this.ruptureWinner=null;
    this.ruptureFinishTimer=0;
    this.tutorial=null;
    this.showcaseCar=null;
    const isRupture=gameType==='rupture';
    if(isRupture){
      this.ruptureArena=new RuptureArena(
        this.scene,
        this.world,
        GAME_MODE_RULES.rupture,
        {mapId:this.selectedMapId},
      );
    }
    this.navigator=new GridNavigator(this.world);
    const isBattle=gameType==='battle';
    const isFreeForAll=gameType==='ffa';
    const isCombat=isBattle||isFreeForAll;
    const modeRules=GAME_MODE_RULES[gameType]||GAME_MODE_RULES.practice;
    const playerFfaSpawn=isFreeForAll?this.findFreeForAllSpawn(0):null;
    const playerRuptureSpawn=isRupture?this.ruptureArena.spawnFor(0):null;
    const playerPosition=isBattle?this.findBattleSpawn('blue',0)
      :playerRuptureSpawn?.position||playerFfaSpawn?.position||this.world.spawn;
    const playerYaw=isBattle?0:playerRuptureSpawn?.yaw??playerFfaSpawn?.yaw??this.world.spawnYaw;
    const playerCar=createCar(this.selectedCarId,{loadout:this.selectedLoadout});
    this.scene.add(playerCar);
    this.player=new ArcadeVehicle(playerCar,this.world,{
      position:playerPosition,
      yaw:playerYaw,
      isPlayer:true,
      onImpact:(force,position)=>this.handleImpact(force,position),
      onLand:(airTime,force)=>this.handleLanding(airTime,force),
      onJump:()=>this.audio.blip(260,.12,'sine',.065),
      onDodge:(dodge)=>this.handleAirDodge(dodge),
      onDestroyed:(vehicle,position)=>this.handleVehicleDestroyed(vehicle,position),
      respawnDelay:modeRules.respawnDelay,
      respawnProtection:modeRules.respawnProtection,
      getRespawn:isBattle?(vehicle)=>this.findSafeBattleRespawn('blue',0,vehicle)
        :isFreeForAll?(vehicle)=>this.findFreeForAllSpawn(0,vehicle):null,
    });
    this.player.displayName='VOS';
    this.player.gameMode=gameType;
    if(isCombat){
      this.player.combatStats={kills:0,deaths:0,assists:0};
      if(isBattle){
        this.player.teamId='blue';
        this.attachTeamRing(this.player,'#49dff5');
      }
    }
    this.vehicles.push(this.player);

    const botColors=['#f1c33c','#35b488','#df5b90','#477fdd','#eb713f'];
    const botAccents=['#4d3d9e','#f5e96d','#65e7f0','#ffdb4d','#4d3d9e'];
    const allyColors=['#2b9ee8','#39c9d0','#536ff2','#48a6bf'];
    const allyAccents=['#baf8ff','#d4ffff','#c8d1ff','#e3fbff'];
    const enemyColors=['#e3474f','#ed653f','#ca3f72','#f07d32','#c93442'];
    const enemyAccents=['#ffd5cf','#ffe08f','#ffc5e0','#fff0b0','#ffb8bd'];
    const botCount=isBattle?9:gameType==='tutorial'?0:5;
    const botAppearances=createBotAppearanceDeck({
      cars:CARS,
      cosmeticSlots:COSMETIC_SLOTS,
      count:botCount,
      defaultLoadoutFor:(car)=>this.defaultLoadout(car.id),
    });
    for(let i=0;i<botCount;i+=1){
      const appearance=botAppearances[i];
      const carConfig=getCar(appearance.carId);
      const teamId=isBattle?(i<4?'blue':'red'):null;
      const teamIndex=isBattle?(i<4?i+1:i-4):i;
      const paletteIndex=isBattle?teamIndex:i;
      const colors=teamId==='blue'?allyColors:teamId==='red'?enemyColors:botColors;
      const accents=teamId==='blue'?allyAccents:teamId==='red'?enemyAccents:botAccents;
      const botCar=createCar(carConfig.id,{
        color:colors[paletteIndex%colors.length],
        accent:accents[paletteIndex%accents.length],
        scale:.98,
        loadout:appearance.loadout,
      });
      const ffaSpawn=isFreeForAll?this.findFreeForAllSpawn(i+1):null;
      const ruptureSpawn=isRupture?this.ruptureArena.spawnFor(i+1):null;
      const position=isBattle?this.findBattleSpawn(teamId,teamIndex)
        :ruptureSpawn?.position||ffaSpawn?.position||this.findSpawn(i);
      const yaw=isBattle?(teamId==='blue'?0:Math.PI)
        :ruptureSpawn?.yaw??ffaSpawn?.yaw??Math.random()*Math.PI*2;
      this.scene.add(botCar);
      const botVehicle=new ArcadeVehicle(botCar,this.world,{
        position,
        yaw,
        onImpact:(force,pos)=>{if(force>6)this.effects.burst(pos,'#fff08a',5,3);},
        onDestroyed:(vehicle,destroyedAt)=>this.handleVehicleDestroyed(vehicle,destroyedAt),
        respawnDelay:modeRules.respawnDelay,
        respawnProtection:modeRules.respawnProtection,
        getRespawn:isBattle?(vehicle)=>this.findSafeBattleRespawn(teamId,teamIndex,vehicle)
          :isFreeForAll?(vehicle)=>this.findFreeForAllSpawn(i+1,vehicle):null,
      });
      botVehicle.displayName=isBattle?`${teamId==='blue'?'AZUL':'ROJO'} ${teamIndex+1}`:carConfig.name.toUpperCase();
      botVehicle.gameMode=gameType;
      if(isCombat){
        botVehicle.combatStats={kills:0,deaths:0,assists:0};
        if(isBattle){
          botVehicle.teamId=teamId;
          this.attachTeamRing(botVehicle,teamId==='blue'?'#49dff5':'#ff5d75');
        }
      }
      this.vehicles.push(botVehicle);
      this.bots.push(new BotBrain(
        botVehicle,
        this.world,
        i,
        this.navigator,
        this.botDifficulty,
      ));
      if(gameType==='practice'||isCombat||isRupture)this.attachHealthBar(
        botVehicle,
        isBattle?(teamId==='blue'?'#49dff5':'#ff5d75'):null,
      );
    }

    if(isRupture){
      this.vehicles.forEach((vehicle,index)=>{
        vehicle.ruptureSlot=index;
        vehicle.ruptureFuses=modeRules.fuses;
        vehicle.ruptureInstability=0;
        vehicle.ruptureHitHold=0;
        vehicle.ruptureLandedPlatform=0;
        vehicle.ruptureRespawnPending=false;
        vehicle.ruptureRespawnTimer=0;
        vehicle.ruptureEliminated=false;
        vehicle.ruptureReachedPlatform=0;
        vehicle.ruptureTransferAssistIndex=null;
        vehicle.health=100;
      });
    }

    if(gameType==='chase'){
      this.vehicles.forEach((vehicle)=>{
        this.chaseScores.set(vehicle,0);
        vehicle.chaseInstability=0;
        vehicle.chasePressureHold=0;
        vehicle.chaseHitCooldown=0;
        vehicle.chaseSlowTimer=0;
        vehicle.chaseNitroLockTimer=0;
      });
      this.chaseMarker=createChaseMarker();
      this.scene.add(this.chaseMarker);
      // La primera ronda comienza con un rival escapando para que el jugador
      // entienda el objetivo antes de convertirse en perseguido.
      this.setPursued(this.vehicles[1+Math.floor(Math.random()*(this.vehicles.length-1))],true);
    }

    if(gameType==='tutorial'){
      this.tutorial={
        stepIndex:0,
        hold:0,
        completed:false,
        completionTime:0,
      };
    }

    this.resetCombatAim();
    this.camera.position.copy(this.player.car.position).add(new THREE.Vector3(0,3,5.4));
    this.cameraTarget.copy(this.player.car.position);
    this.effects.clear();this.projectiles.clear();
    if(this.powerupsEnabled())this.initializePowerups();
    this.audio.ensure();
    this.vehicles.forEach((vehicle)=>{
      this.preloadVehicleAudio(vehicle);
    });
    this.audio.pauseMusic(false);
    this.audio.playRandomLevelMusic();
    this.callbacks.onStart?.(this.getMatchState());
    this.beginStartCountdown(3.6);
  }

  findSpawn(index) {
    const angle=(index/5)*Math.PI*2+.4;
    for(let radius=5;radius<14;radius+=2){
      const candidate=this.world.spawn.clone().add(new THREE.Vector3(Math.cos(angle)*radius,0,Math.sin(angle)*radius));
      candidate.x=THREE.MathUtils.clamp(candidate.x,-this.world.width/2+2,this.world.width/2-2);
      candidate.z=THREE.MathUtils.clamp(candidate.z,-this.world.depth/2+2,this.world.depth/2-2);
      if(!pointInsideObstacle(candidate,this.world.obstacles,1.3))return candidate;
    }
    return new THREE.Vector3((index-2)*2.2,0,this.world.depth/2-3);
  }

  findBattleSpawn(teamId,index) {
    const side=teamId==='blue'?1:-1;
    const offsets=[-8,-4,0,4,8];
    const candidate=new THREE.Vector3(
      THREE.MathUtils.clamp(offsets[index]||0,-this.world.width/2+3,this.world.width/2-3),
      .02,
      side*(this.world.depth/2-5.5),
    );
    const safe=this.navigator.nearestWalkablePoint(candidate);
    if(safe){safe.y=.02;return safe;}
    return this.world.spawn.clone().add(new THREE.Vector3((index-2)*2.2,0,side*4));
  }

  findSafeBattleRespawn(teamId,index,excludedVehicle=null) {
    const candidates=[0,1,2,3,4].map((slot)=>this.findBattleSpawn(teamId,slot));
    const activeVehicles=this.vehicles.filter((vehicle)=>vehicle!==excludedVehicle&&!vehicle.disabled);
    const enemies=activeVehicles.filter((vehicle)=>vehicle.teamId!==teamId);
    let best=candidates[index]||candidates[0]||this.world.spawn.clone();
    let bestScore=-Infinity;
    candidates.forEach((candidate,slot)=>{
      const nearestEnemy=enemies.length
        ?Math.min(...enemies.map((vehicle)=>candidate.distanceTo(vehicle.car.position)))
        :20;
      const nearestVehicle=activeVehicles.length
        ?Math.min(...activeVehicles.map((vehicle)=>candidate.distanceTo(vehicle.car.position)))
        :10;
      const nearestProjectile=this.projectiles.projectiles.length
        ?Math.min(...this.projectiles.projectiles.map((projectile)=>candidate.distanceTo(projectile.root.position)))
        :Infinity;
      const projectilePenalty=nearestProjectile<7?(7-nearestProjectile)*3:0;
      const preferredSlotBonus=slot===index ? .25 : 0;
      const score=nearestEnemy*2.2+nearestVehicle*.4-projectilePenalty+preferredSlotBonus;
      if(score>bestScore){bestScore=score;best=candidate;}
    });
    best.y=.02;
    return {position:best,yaw:teamId==='blue'?0:Math.PI};
  }

  findFreeForAllSpawn(seed=0,excludedVehicle=null) {
    const candidates=[];
    const phase=(seed*.61803398875%1)*Math.PI*2;
    const goldenAngle=Math.PI*(3-Math.sqrt(5));
    for(let index=0;index<24;index+=1){
      const ring=.28+(index%3)*.085;
      const angle=phase+index*goldenAngle;
      const candidate=new THREE.Vector3(
        Math.cos(angle)*this.world.width*ring,
        .02,
        Math.sin(angle)*this.world.depth*ring,
      );
      const safe=this.navigator.nearestWalkablePoint(candidate);
      if(!safe)continue;
      safe.y=.02;
      if(!candidates.some((other)=>other.distanceToSquared(safe)<2.25))candidates.push(safe);
    }
    const activeVehicles=this.vehicles.filter((vehicle)=>vehicle!==excludedVehicle&&!vehicle.disabled);
    let best=null;
    let bestScore=-Infinity;
    candidates.forEach((candidate,index)=>{
      const nearestVehicle=activeVehicles.length
        ?Math.min(...activeVehicles.map((vehicle)=>candidate.distanceTo(vehicle.car.position)))
        :18-index*.03;
      const nearestProjectile=this.projectiles.projectiles.length
        ?Math.min(...this.projectiles.projectiles.map((projectile)=>candidate.distanceTo(projectile.root.position)))
        :Infinity;
      const projectilePenalty=nearestProjectile<7?(7-nearestProjectile)*3:0;
      const centerPenalty=candidate.length()*.025;
      const score=nearestVehicle*2.2-projectilePenalty-centerPenalty;
      if(score>bestScore){bestScore=score;best=candidate;}
    });
    const position=best||this.world.spawn.clone();
    position.y=.02;
    return {position,yaw:Math.atan2(position.x,position.z)};
  }

  setPursued(vehicle,initial=false) {
    if(!vehicle||vehicle===this.chasePursued&&!initial)return;
    const previous=this.chasePursued;
    this.chasePursued=vehicle;
    [previous,vehicle].filter(Boolean).forEach((entry)=>{
      entry.chaseInstability=0;
      entry.chasePressureHold=0;
      entry.chaseSlowTimer=0;
      entry.chaseNitroLockTimer=0;
    });
    const rules=GAME_MODE_RULES.chase;
    this.chaseTransferCooldown=initial?rules.tagProtectionInitial:rules.tagProtection;
    if(this.chaseMarker)this.chaseMarker.visible=true;
    const position=vehicle.car.position.clone();
    position.y+=.22;
    this.effects.burst(position,initial?'#fff15e':'#ff9c43',initial?14:20,initial?4:6);
    if(!initial){
      this.audio.blip(vehicle===this.player?175:420,.16,'square',.09);
      if(previous===this.player||vehicle===this.player)this.cameraShake=Math.max(this.cameraShake,.34);
      this.callbacks.onChaseTag?.({
        previousName:previous?.displayName||'—',
        pursuedName:vehicle.displayName||'AUTO',
        pursuedIsPlayer:vehicle===this.player,
      });
    }
  }

  handleChaseContacts(contacts) {
    if(this.chaseLastPair&&!contacts.some(({a,b})=>
      (a===this.chaseLastPair[0]&&b===this.chaseLastPair[1])||(a===this.chaseLastPair[1]&&b===this.chaseLastPair[0])
    ))this.chaseLastPair=null;
    if(this.gameType!=='chase'||this.chaseTransferCooldown>0||!this.chasePursued
      ||this.chasePursued.powerupShieldTimer>0)return;
    const contact=contacts
      .filter(({a,b})=>(a===this.chasePursued||b===this.chasePursued)&&!(
        this.chaseLastPair&&((a===this.chaseLastPair[0]&&b===this.chaseLastPair[1])||(a===this.chaseLastPair[1]&&b===this.chaseLastPair[0]))
      ))
      .sort((first,second)=>second.closingSpeed-first.closingSpeed)[0];
    if(!contact)return;
    const next=contact.a===this.chasePursued?contact.b:contact.a;
    this.chaseLastPair=[contact.a,contact.b];
    this.effects.burst(contact.position,'#fff4a3',16,5.5);
    this.setPursued(next);
  }

  updateChaseCombatState(dt) {
    if(this.gameType!=='chase'&&this.onlineSession?.match.mode!=='chase')return;
    const rules=GAME_MODE_RULES.chase;
    this.vehicles.forEach((vehicle)=>{
      vehicle.chaseHitCooldown=Math.max(0,(vehicle.chaseHitCooldown||0)-dt);
      vehicle.chasePressureHold=Math.max(0,(vehicle.chasePressureHold||0)-dt);
      vehicle.chaseSlowTimer=Math.max(0,(vehicle.chaseSlowTimer||0)-dt);
      vehicle.chaseNitroLockTimer=Math.max(0,(vehicle.chaseNitroLockTimer||0)-dt);
      if(vehicle.chasePressureHold<=0)vehicle.chaseInstability=Math.max(0,(vehicle.chaseInstability||0)-rules.instabilityDecay*dt);
    });
  }

  applyChaseControlEffects(vehicle,controls) {
    if(this.gameType!=='chase'&&this.onlineSession?.match.mode!=='chase')return controls;
    const rules=GAME_MODE_RULES.chase;
    const adjusted={...controls};
    if(vehicle.chaseNitroLockTimer>0)adjusted.nitroDisabled=true;
    if(vehicle.chaseSlowTimer>0){
      adjusted.accelerationScale=(adjusted.accelerationScale??1)*rules.slowAccelerationScale;
      adjusted.speedScale=(adjusted.speedScale??1)*rules.slowTopSpeedScale;
      adjusted.steer=(adjusted.steer||0)*rules.slowSteeringScale;
    }
    return adjusted;
  }

  handleChaseProjectileHit({target,owner,direction,position}) {
    if(this.gameType!=='chase'||!this.chasePursued)return 'ignore';
    const rules=GAME_MODE_RULES.chase;
    const ownerIsPursued=owner===this.chasePursued;
    const targetIsPursued=target===this.chasePursued;
    // Los perseguidores no pueden molestarse entre sí. El proyectil continúa
    // su recorrido hasta encontrar al objetivo o al escenario.
    if(!ownerIsPursued&&!targetIsPursued)return 'ignore';
    if(target.chaseHitCooldown>0)return 'handled';
    target.chaseHitCooldown=rules.hitCooldown;
    if(targetIsPursued){
      target.velocity.addScaledVector(direction,rules.pursuedImpulse/target.config.mass);
      target.velocity.y+=rules.pursuedLift/target.config.mass;
      target.chaseInstability=Math.min(rules.instabilityThreshold,(target.chaseInstability||0)+rules.instabilityPerHit);
      target.chasePressureHold=rules.pressureHold;
      if(target.chaseInstability>=rules.instabilityThreshold){
        target.chaseInstability=0;
        target.chaseNitroLockTimer=rules.nitroLockDuration;
        target.chaseSlowTimer=Math.max(target.chaseSlowTimer,rules.pursuedSlowDuration);
        this.effects.burst(position,'#74efff',18,6.2);
        this.audio.blip(165,.2,'sawtooth',.1);
        if(target===this.player)this.cameraShake=Math.max(this.cameraShake,.42);
      }
    }else{
      target.velocity.addScaledVector(direction,rules.defensiveImpulse/target.config.mass);
      target.velocity.y+=rules.defensiveLift/target.config.mass;
      target.chaseSlowTimer=Math.max(target.chaseSlowTimer,rules.defensiveSlowDuration);
      if(target===this.player)this.cameraShake=Math.max(this.cameraShake,.24);
    }
    return 'handled';
  }

  updateChaseMode(dt) {
    if(this.gameType!=='chase'||!this.chasePursued)return;
    this.chaseTransferCooldown=Math.max(0,this.chaseTransferCooldown-dt);
    this.chaseRemaining=Math.max(0,this.chaseRemaining-dt);
    this.chaseScores.set(this.chasePursued,(this.chaseScores.get(this.chasePursued)||0)+dt);
    if(this.chaseMarker){
      const height=this.chasePursued.car.userData.collisionHeight+.72;
      this.chaseMarker.position.copy(this.chasePursued.car.position);
      this.chaseMarker.position.y+=height+Math.sin(this.gameElapsed*5)*.08;
      this.chaseMarker.rotation.y=this.gameElapsed*1.8;
      this.chaseMarker.userData.ring.scale.setScalar(1+Math.sin(this.gameElapsed*7)*.09);
    }
    if(this.chaseRemaining<=0)this.finishChase();
  }

  updateBattleMode(dt) {
    if(this.gameType!=='battle'||this.mode!=='game')return;
    this.battleRemaining=Math.max(0,this.battleRemaining-dt);
    if(this.battleRemaining<=0)this.finishBattle();
  }

  updateFreeForAllMode(dt) {
    if(this.gameType!=='ffa'||this.mode!=='game'||this.ffaOvertime)return;
    this.ffaRemaining=Math.max(0,this.ffaRemaining-dt);
    if(this.ffaRemaining>0)return;
    const ranked=[...this.vehicles].sort((a,b)=>(b.combatStats?.kills||0)-(a.combatStats?.kills||0)
      ||(b.combatStats?.assists||0)-(a.combatStats?.assists||0)
      ||(a.combatStats?.deaths||0)-(b.combatStats?.deaths||0));
    const topKills=ranked[0]?.combatStats?.kills||0;
    const tied=ranked.filter((vehicle)=>(vehicle.combatStats?.kills||0)===topKills);
    if(tied.length===1)this.finishFreeForAll(tied[0]);
    else{
      this.ffaOvertime=true;
      this.ffaOvertimeContenders=new Set(tied);
    }
  }

  ruptureImpactScale(vehicle){
    const instability=THREE.MathUtils.clamp((vehicle.ruptureInstability||0)/100,0,1);
    let scale=.95+instability*1.75;
    if(vehicle.handbraking&&vehicle.grounded)scale*=.62;
    if(!vehicle.grounded)scale*=.9;
    return scale;
  }

  handleRuptureProjectileHit({target,owner,direction,position}){
    if(this.gameType!=='rupture'||target.ruptureEliminated)return 'ignore';
    const rules=GAME_MODE_RULES.rupture;
    target.ruptureInstability=Math.min(
      100,
      (target.ruptureInstability||0)+rules.instabilityPerProjectile,
    );
    target.ruptureHitHold=rules.instabilityHold;
    const impactScale=this.ruptureImpactScale(target);
    const push=1.55+target.ruptureInstability*.017;
    target.velocity.addScaledVector(direction,push*impactScale/target.config.mass);
    target.velocity.y+=.24*impactScale/target.config.mass;
    target.lastDamager=owner&&owner!==target?owner:target.lastDamager;
    if(target===this.player)this.cameraShake=Math.max(this.cameraShake,.26+impactScale*.1);
    return {
      handled:true,
      impactStrength:THREE.MathUtils.clamp(.88*impactScale,.5,1.6),
    };
  }

  handleRuptureContacts(contacts){
    if(this.gameType!=='rupture')return;
    const rules=GAME_MODE_RULES.rupture;
    contacts.forEach((contact)=>{
      if(contact.closingSpeed<1.8)return;
      for(const [target,source] of [[contact.a,contact.b],[contact.b,contact.a]]){
        if(target.ruptureEliminated||target.ruptureRamCooldown>0)continue;
        target.ruptureRamCooldown=.16;
        const attackMultiplier=source.airDodgeImpactTimer>0?1.35:1;
        const boostMultiplier=(source.boosting?2.15:1)*attackMultiplier;
        const instabilityGain=THREE.MathUtils.clamp(
          (contact.closingSpeed-1.2)*.92*boostMultiplier,
          2,
          source.boosting||source.airDodgeImpactTimer>0?18:11,
        );
        target.ruptureInstability=Math.min(100,(target.ruptureInstability||0)+instabilityGain);
        target.ruptureHitHold=rules.instabilityHold;
        const direction=target.car.position.clone().sub(source.car.position);
        direction.y=.12;
        if(direction.lengthSq()<.001)direction.set(1,.12,0);
        direction.normalize();
        const impactScale=this.ruptureImpactScale(target)*boostMultiplier;
        target.velocity.addScaledVector(
          direction,
          (.3+contact.closingSpeed*.075)*impactScale/target.config.mass,
        );
        target.applyProjectileImpact(
          direction,
          contact.position,
          THREE.MathUtils.clamp(.3+contact.closingSpeed*.05,.3,1.1)*impactScale,
        );
      }
    });
  }

  ruptureBotControls(brain,dt){
    const vehicle=brain.vehicle;
    const arena=this.ruptureArena;
    const state=arena.state();
    const difficulty=brain.difficulty||getBotDifficulty(this.botDifficulty);
    brain.fireClock=Math.max(0,(brain.fireClock||0)-dt);
    brain.jumpCooldown=Math.max(0,(brain.jumpCooldown||0)-dt);
    brain.behaviorClock+=dt;

    if(vehicle.grounded&&!vehicle.wheelsGrounded){
      const recovery=vehicle.airRecoveryError();
      const jump=brain.jumpCooldown<=0;
      if(jump)brain.jumpCooldown=.58+brain.random()*.2;
      return {
        throttle:0,
        steer:0,
        jump,
        boost:false,
        handbrake:false,
        fire:false,
        airPitch:THREE.MathUtils.clamp(recovery.pitch*1.45,-1,1),
        airYaw:0,
        airRoll:THREE.MathUtils.clamp(recovery.roll*1.45,-1,1),
        speedScale:1,
        accelerationScale:1,
      };
    }

    const currentPlatform=arena.platformAt(vehicle.car.position);
    const hasNext=state.nextPlatformIndex!==null;
    const aboveDestination=hasNext&&Boolean(
      arena.tileAtPosition(vehicle.car.position,state.nextPlatformIndex,.12),
    );
    const arrived=hasNext&&currentPlatform===state.nextPlatformIndex;
    const transferring=hasNext&&!arrived;
    const destinationIndex=hasNext?state.nextPlatformIndex:state.platformIndex;
    const safePlatformIndex=arrived?state.nextPlatformIndex:state.platformIndex;
    let target=arena.spawnFor(vehicle.ruptureSlot,destinationIndex).position;
    const candidates=this.vehicles.filter((other)=>
      other!==vehicle
      &&!other.disabled
      &&!other.ruptureEliminated
      &&arena.platformAt(other.car.position)===currentPlatform
    );
    const opponent=candidates.sort((first,second)=>
      vehicle.car.position.distanceToSquared(first.car.position)
      -vehicle.car.position.distanceToSquared(second.car.position)
    )[0]||null;

    let edgeDanger=false;
    if(!transferring){
      // En una plataforma no sirve el navegador terrestre: elegimos puntos
      // que sabemos que pertenecen a baldosas activas y anticipamos medio
      // segundo de movimiento para frenar antes del vacío.
      const anchor=arena.spawnFor(vehicle.ruptureSlot,safePlatformIndex).position;
      const patrolAngle=brain.behaviorClock*.62+brain.seed*1.37;
      const patrol=anchor.clone().add(new THREE.Vector3(
        Math.cos(patrolAngle)*.52,
        0,
        Math.sin(patrolAngle)*.52,
      ));
      target=arena.tileAtPosition(patrol,safePlatformIndex,.04)?patrol:anchor;
      const predicted=vehicle.car.position.clone().addScaledVector(
        vehicle.velocity,
        difficulty.edgeLookAhead,
      );
      edgeDanger=!arena.isSafePosition(
        vehicle.car.position,
        safePlatformIndex,
        difficulty.edgeClearance,
      )||!arena.isSafePosition(
        predicted,
        safePlatformIndex,
        difficulty.edgeClearance,
      );
      if(edgeDanger)target=anchor;
    }

    const direction=target.clone().sub(vehicle.car.position);
    direction.y=0;
    const distance=direction.length();
    const desiredYaw=Math.atan2(-direction.x,-direction.z);
    const yawDelta=normalizeAngle(desiredYaw-vehicle.yaw);
    const steer=THREE.MathUtils.clamp(-yawDelta*difficulty.steeringGain,-1,1);
    const aligned=Math.abs(yawDelta)<.48;
    const transferReady=state.phase.id!=='warning'
      ||state.phase.remaining<difficulty.transferReadyWindow;
    if(transferring&&transferReady)vehicle.ruptureTransferAssistIndex=state.nextPlatformIndex;
    const jump=Boolean(
      transferring
      &&transferReady
      &&vehicle.grounded
      &&brain.jumpCooldown<=0
      &&aligned
      &&distance>3.6
    );
    if(jump)brain.jumpCooldown=1.1;

    let fire=false;
    let aimX=0;
    let aimY=0;
    let aimZ=0;
    if(opponent&&brain.fireClock<=0){
      const aim=opponent.car.position.clone().sub(vehicle.car.position);
      const aimLength=Math.max(.0001,aim.length());
      aimX=aim.x/aimLength;
      aimY=aim.y/aimLength;
      aimZ=aim.z/aimLength;
      const aimYaw=Math.atan2(-aim.x,-aim.z);
      const aimError=Math.abs(normalizeAngle(aimYaw-vehicle.yaw));
      fire=aim.length()<18&&aimError<.25*difficulty.aimAngleScale;
      if(fire){
        brain.fireClock=(.3+brain.random()*.28)*difficulty.fireDelayScale;
      }
    }

    const boost=Boolean(
      transferring
      &&transferReady
      &&vehicle.nitro>.5
      &&!aboveDestination
      &&(!vehicle.grounded||aligned)
      &&distance>2.2
    );
    const targetHeight=arena.getStage(destinationIndex)?.top??vehicle.car.position.y;
    if(transferring&&!vehicle.grounded){
      if(aboveDestination){
        const braking=Math.exp(-5.2*dt);
        vehicle.velocity.x*=braking;
        vehicle.velocity.z*=braking;
      }else{
        const flightDirection=target.clone().sub(vehicle.car.position);
        flightDirection.y=0;
        if(flightDirection.lengthSq()>.01){
          const targetDistance=flightDirection.length();
          flightDirection.normalize();
          const desiredSpeed=THREE.MathUtils.clamp(
            targetDistance*2.5,
            9,
            difficulty.transferMaximumSpeed,
          );
          const desiredX=flightDirection.x*desiredSpeed;
          const desiredZ=flightDirection.z*desiredSpeed;
          const guidance=1-Math.exp(-dt*difficulty.transferGuidance);
          vehicle.velocity.x=THREE.MathUtils.lerp(vehicle.velocity.x,desiredX,guidance);
          vehicle.velocity.z=THREE.MathUtils.lerp(vehicle.velocity.z,desiredZ,guidance);
        }
      }
      if(!aboveDestination&&vehicle.car.position.y<targetHeight+.35)vehicle.velocity.y+=5.4*dt;
    }
    return {
      throttle:vehicle.grounded
        ?edgeDanger?(vehicle.forwardSpeed>2.2?-.48:.34)
          :transferring
            ?transferReady?(aligned?1:.58)
              :aligned?0:.22
            :distance<.3?0:aligned?difficulty.patrolThrottle:.26
        :boost?.7:0,
      steer,
      jump,
      boost,
      handbrake:vehicle.grounded&&(edgeDanger
        ||transferring&&!transferReady
        ||Math.abs(yawDelta)>.85&&Math.abs(vehicle.forwardSpeed)>6),
      fire,
      aimX,
      aimY,
      aimZ,
      airYaw:steer,
      airRoll:steer,
      airPitch:targetHeight>vehicle.car.position.y+.5?.32
        :targetHeight<vehicle.car.position.y-.75?-.2:0,
      speedScale:transferring?1:.72,
      accelerationScale:transferring?1:.88,
    };
  }

  playRuptureFallEffect(vehicle,position){
    const loadout=vehicle.car.userData.loadout||this.defaultLoadout(vehicle.config.id);
    const explosion=getDeathExplosion(loadout.explosionId);
    this.effects.elimination(position,explosion);
    const distance=this.player?position.distanceTo(this.player.car.position):0;
    this.audio.explosion(explosion,THREE.MathUtils.clamp(1-distance/24,.22,1));
    if(vehicle===this.player){
      this.cameraShake=.62;
      this.input.stopRumble();
      this.input.rumble({
        duration:520,weakMagnitude:.82,strongMagnitude:1,priority:4,key:'player-rupture-fall',
      });
    }
  }

  eliminateRuptureVehicle(vehicle){
    if(vehicle.disabled||vehicle.ruptureEliminated)return;
    const arenaState=this.ruptureArena.state();
    const position=vehicle.car.position.clone();
    const suddenDeath=arenaState.suddenDeath;
    vehicle.ruptureFuses=suddenDeath?0:Math.max(0,(vehicle.ruptureFuses||0)-1);
    vehicle.ruptureEliminated=vehicle.ruptureFuses<=0;
    vehicle.ruptureRespawnPending=!vehicle.ruptureEliminated;
    vehicle.ruptureRespawnTimer=1.25;
    resetVehiclePowerups(vehicle);
    vehicle.disabled=true;
    vehicle.respawnTimer=Infinity;
    vehicle.velocity.set(0,0,0);
    vehicle.boosting=false;
    vehicle.car.visible=false;
    this.playRuptureFallEffect(vehicle,position);
    this.callbacks.onRuptureEvent?.({
      type:vehicle.ruptureEliminated?'eliminated':'fuse',
      name:vehicle.displayName,
      isPlayer:vehicle===this.player,
      fuses:vehicle.ruptureFuses,
    });
    if(vehicle===this.player&&vehicle.ruptureEliminated)this.ruptureFinishTimer=.85;
  }

  respawnRuptureVehicle(vehicle){
    const spawn=this.ruptureArena.spawnFor(vehicle.ruptureSlot);
    const rules=GAME_MODE_RULES.rupture;
    const instability=vehicle.ruptureInstability||0;
    vehicle.reset(spawn.position,spawn.yaw,false);
    vehicle.ruptureRespawnPending=false;
    vehicle.ruptureRespawnTimer=0;
    vehicle.ruptureInstability=Math.max(0,instability-24);
    vehicle.ruptureHitHold=0;
    vehicle.ruptureLandedPlatform=this.ruptureArena.destinationIndex();
    vehicle.ruptureReachedPlatform=Math.max(
      vehicle.ruptureReachedPlatform||0,
      this.ruptureArena.destinationIndex(),
    );
    vehicle.nitro=Math.max(vehicle.nitro,rules.minimumTransferNitro);
    vehicle.invulnerableTimer=0;
    vehicle.collisionGhostTimer=0;
  }

  handleRuptureArenaEvents(){
    const rules=GAME_MODE_RULES.rupture;
    this.ruptureArena.consumeEvents().forEach((event)=>{
      if(event.type==='reveal'){
        this.vehicles.forEach((vehicle)=>{
          if(!vehicle.ruptureEliminated)vehicle.nitro=Math.max(vehicle.nitro,rules.minimumTransferNitro);
        });
        const center=this.ruptureArena.getStage(event.platformIndex).center.clone();
        center.y=this.ruptureArena.getStage(event.platformIndex).top+.2;
        this.effects.burst(center,'#fff16a',22,7);
        this.audio.blip(520,.22,'square',.1);
        this.callbacks.onRuptureEvent?.({type:'reveal'});
      }else if(event.type==='collapse'){
        this.cameraShake=Math.max(this.cameraShake,.34);
        this.audio.blip(92,.36,'sawtooth',.12);
        this.callbacks.onRuptureEvent?.({type:'collapse'});
      }else if(event.type==='stage'){
        this.callbacks.onRuptureEvent?.({
          type:'stage',
          platform:event.platformIndex+1,
        });
      }else if(event.type==='final-warning'){
        this.audio.blip(185,.42,'square',.12);
        this.callbacks.onRuptureEvent?.({type:'final-warning'});
      }else if(event.type==='final-ring'){
        this.cameraShake=Math.max(this.cameraShake,.28);
        this.audio.blip(72+event.ringsRemaining*16,.24,'sawtooth',.11);
      }
    });
  }

  updateRuptureMode(dt){
    if(this.gameType!=='rupture'||!this.ruptureArena||this.mode!=='game')return;
    const rules=GAME_MODE_RULES.rupture;
    this.ruptureArena.update(dt);
    if(this.rupturePowerupDirector?.update())this.syncPowerupCrateVisuals(this.powerupCrates);
    this.handleRuptureArenaEvents();
    const state=this.ruptureArena.state();
    this.vehicles.forEach((vehicle)=>{
      vehicle.ruptureRamCooldown=Math.max(0,(vehicle.ruptureRamCooldown||0)-dt);
      vehicle.ruptureHitHold=Math.max(0,(vehicle.ruptureHitHold||0)-dt);
      if(vehicle.ruptureHitHold<=0&&!vehicle.disabled){
        vehicle.ruptureInstability=Math.max(
          0,
          (vehicle.ruptureInstability||0)-rules.instabilityDecay*dt,
        );
      }
      if(vehicle.ruptureRespawnPending){
        vehicle.ruptureRespawnTimer=Math.max(0,vehicle.ruptureRespawnTimer-dt);
        if(vehicle.ruptureRespawnTimer<=0)this.respawnRuptureVehicle(vehicle);
        return;
      }
      if(vehicle.disabled||vehicle.ruptureEliminated)return;
      if(vehicle.car.position.y<state.killHeight){
        this.eliminateRuptureVehicle(vehicle);
        return;
      }
      const landedPlatform=vehicle.grounded
        ?this.ruptureArena.platformAt(vehicle.car.position)
        :null;
      if(landedPlatform===null||landedPlatform===vehicle.ruptureLandedPlatform)return;
      vehicle.ruptureLandedPlatform=landedPlatform;
      vehicle.ruptureTransferAssistIndex=null;
      vehicle.ruptureReachedPlatform=Math.max(vehicle.ruptureReachedPlatform||0,landedPlatform);
      vehicle.ruptureInstability=Math.max(0,(vehicle.ruptureInstability||0)-22);
      vehicle.nitro=Math.min(100,vehicle.nitro+rules.landingNitro);
      vehicle.invulnerableTimer=0;
      vehicle.collisionGhostTimer=0;
      this.effects.burst(vehicle.car.position.clone().setY(vehicle.car.position.y+.08),'#8ff7ff',10,3.2);
      if(vehicle===this.player)this.callbacks.onRuptureEvent?.({
        type:'landed',
        platform:landedPlatform+1,
      });
    });

    const contenders=this.vehicles.filter((vehicle)=>!vehicle.ruptureEliminated);
    if(contenders.length===1&&this.gameElapsed>5){
      this.ruptureWinner=contenders[0];
      this.ruptureFinishTimer=this.ruptureFinishTimer||.85;
    }
    if(this.ruptureFinishTimer>0){
      this.ruptureFinishTimer=Math.max(0,this.ruptureFinishTimer-dt);
      if(this.ruptureFinishTimer<=0){
        if(!this.ruptureWinner){
          this.ruptureWinner=[...contenders].sort((a,b)=>
            (b.ruptureReachedPlatform||0)-(a.ruptureReachedPlatform||0)
            ||(b.ruptureFuses||0)-(a.ruptureFuses||0)
            ||(a.ruptureInstability||0)-(b.ruptureInstability||0)
          )[0]||null;
        }
        this.finishRupture(this.ruptureWinner);
      }
    }
  }

  getTutorialState(){
    if(!this.tutorial)return null;
    const step=TUTORIAL_STEPS[this.tutorial.stepIndex]||null;
    return {
      completed:this.tutorial.completed,
      stepIndex:this.tutorial.stepIndex,
      total:TUTORIAL_STEPS.length,
      progress:step?.hold
        ?THREE.MathUtils.clamp(this.tutorial.hold/step.hold,0,1)
        :0,
      step:step?{
        id:step.id,
        title:step.title,
        instruction:step.instruction,
        keyboard:step.keyboard,
        gamepad:step.gamepad,
      }:null,
    };
  }

  updateTutorial(dt,controls){
    const tutorial=this.tutorial;
    if(!tutorial||tutorial.completed||!this.player||this.player.disabled)return;
    const step=TUTORIAL_STEPS[tutorial.stepIndex];
    if(!step)return;
    const speed=Math.abs(this.player.forwardSpeed);
    let active=false;
    switch(step.id){
      case 'accelerate':
        active=controls.throttle>.55&&this.player.forwardSpeed>2;
        break;
      case 'steer':
        active=Math.abs(controls.steer)>.45&&speed>2;
        break;
      case 'reverse':
        active=controls.throttle<-.45&&this.player.forwardSpeed<-.8;
        break;
      case 'handbrake':
        active=controls.handbrake&&Math.abs(controls.steer)>.22&&speed>2.5;
        break;
      case 'jump':
        active=controls.jump;
        break;
      case 'boost':
        active=Boolean(controls.boost)&&!this.player.grounded&&this.player.boosting;
        break;
      case 'air-control':
        active=!this.player.grounded&&(
          Math.abs(controls.airPitch)>.28
          ||Math.abs(controls.airYaw)>.28
          ||Math.abs(controls.airRoll)>.28
        );
        break;
      case 'stabilize':
        active=!this.player.wheelsGrounded&&Boolean(controls.stabilize);
        break;
      case 'fire':
        active=controls.fire;
        break;
      default:
        break;
    }
    tutorial.hold=active
      ?tutorial.hold+dt
      :Math.max(0,tutorial.hold-dt*.45);
    if(!active||(step.hold>0&&tutorial.hold<step.hold))return;
    tutorial.stepIndex+=1;
    tutorial.hold=0;
    tutorial.completed=tutorial.stepIndex>=TUTORIAL_STEPS.length;
    if(tutorial.completed){
      tutorial.completionTime=this.gameElapsed;
      this.audio.blip(760,.14,'square',.085);
      this.audio.blip(980,.2,'triangle',.075);
      const celebration=this.player.car.position.clone().add(new THREE.Vector3(0,.5,0));
      this.effects.burst(celebration,'#fff16a',24,5.2);
      return;
    }
    this.audio.blip(520+tutorial.stepIndex*34,.09,'triangle',.055);
  }

  getMatchState() {
    const matchFinished=this.mode==='post-match'||this.mode==='results';
    if(this.gameType==='rupture'){
      const arena=this.ruptureArena?.state()||{
        phase:{id:'void',label:'FIN',remaining:0,duration:1},
        platformIndex:0,
        platformCount:6,
        platformName:'—',
        nextPlatformName:null,
        final:false,
      };
      let route=null;
      if(this.player&&arena.nextPlatformIndex!==null&&arena.nextPlatformIndex!==undefined){
        const destination=this.ruptureArena?.getStage(arena.nextPlatformIndex);
        if(destination){
          const toDestination=destination.center.clone().sub(this.player.car.position);
          const horizontalDistance=Math.hypot(toDestination.x,toDestination.z);
          const cameraForward=new THREE.Vector3();
          this.camera.getWorldDirection(cameraForward);
          cameraForward.y=0;
          if(cameraForward.lengthSq()<.0001)cameraForward.set(0,0,-1);
          cameraForward.normalize();
          const cameraRight=new THREE.Vector3().crossVectors(cameraForward,CAMERA_UP).normalize();
          route={
            angle:Math.atan2(
              toDestination.dot(cameraRight),
              toDestination.x*cameraForward.x+toDestination.z*cameraForward.z,
            ),
            distance:horizontalDistance,
            rise:destination.top-this.player.car.position.y,
          };
        }
      }
      const scoreboard=this.vehicles.map((vehicle,index)=>({
        name:vehicle.displayName||`AUTO ${index+1}`,
        isPlayer:vehicle===this.player,
        isWinner:vehicle===this.ruptureWinner,
        fuses:vehicle.ruptureFuses||0,
        instability:vehicle.ruptureInstability||0,
        platform:1+(vehicle.ruptureReachedPlatform||0),
        eliminated:Boolean(vehicle.ruptureEliminated),
        color:vehicle.car.userData.bodyColor||vehicle.config.color||'#ffffff',
      })).sort((a,b)=>Number(a.eliminated)-Number(b.eliminated)
        ||b.platform-a.platform
        ||b.fuses-a.fuses
        ||a.instability-b.instability);
      return {
        type:'rupture',
        ...arena,
        fuses:this.player?.ruptureFuses||0,
        instability:this.player?.ruptureInstability||0,
        protection:this.player?.collisionGhostTimer||0,
        respawn:this.player?.ruptureRespawnPending?this.player.ruptureRespawnTimer:0,
        survivors:scoreboard.filter((entry)=>!entry.eliminated).length,
        participantCount:scoreboard.length,
        route,
        scoreboard,
        winner:scoreboard.find((entry)=>entry.isWinner)
          ||(matchFinished?scoreboard[0]:null),
        finished:matchFinished,
      };
    }
    if(this.gameType==='tutorial'){
      return {
        type:'tutorial',
        tutorial:this.getTutorialState(),
      };
    }
    if(this.gameType==='battle'){
      const scoreboard=this.vehicles.map((vehicle,index)=>({
        name:vehicle.displayName||`AUTO ${index+1}`,
        teamId:vehicle.teamId,
        isPlayer:vehicle===this.player,
        kills:vehicle.combatStats?.kills||0,
        deaths:vehicle.combatStats?.deaths||0,
        assists:vehicle.combatStats?.assists||0,
        disabled:vehicle.disabled,
        color:vehicle.teamId==='blue'?'#49dff5':'#ff5d75',
      })).sort((a,b)=>b.kills-a.kills||b.assists-a.assists||a.deaths-b.deaths);
      const winnerTeam=this.battleWinner||(matchFinished
        ?this.battleScore.blue===this.battleScore.red?null:this.battleScore.blue>this.battleScore.red?'blue':'red'
        :null);
      return {
        type:'battle',
        remaining:this.battleRemaining,
        duration:this.battleDuration,
        killLimit:this.battleKillLimit,
        blueScore:this.battleScore.blue,
        redScore:this.battleScore.red,
        respawn:this.player?.disabled?this.player.respawnTimer:0,
        protection:this.player?.invulnerableTimer||0,
        scoreboard,
        teams:{
          blue:scoreboard.filter((entry)=>entry.teamId==='blue'),
          red:scoreboard.filter((entry)=>entry.teamId==='red'),
        },
        finished:matchFinished,
        winnerTeam,
        playerWon:winnerTeam==='blue',
      };
    }
    if(this.gameType==='ffa'){
      const scoreboard=this.vehicles.map((vehicle,index)=>({
        name:vehicle.displayName||`AUTO ${index+1}`,
        isPlayer:vehicle===this.player,
        isWinner:vehicle===this.ffaWinner,
        kills:vehicle.combatStats?.kills||0,
        deaths:vehicle.combatStats?.deaths||0,
        assists:vehicle.combatStats?.assists||0,
        disabled:vehicle.disabled,
        color:vehicle.config.color||'#ffffff',
      })).sort((a,b)=>b.kills-a.kills||b.assists-a.assists||a.deaths-b.deaths||a.name.localeCompare(b.name));
      const playerStats=scoreboard.find((entry)=>entry.isPlayer);
      const playerRank=1+scoreboard.filter((entry)=>entry.kills>(playerStats?.kills||0)).length;
      return {
        type:'ffa',
        remaining:this.ffaRemaining,
        duration:this.ffaDuration,
        killLimit:this.ffaKillLimit,
        overtime:this.ffaOvertime,
        respawn:this.player?.disabled?this.player.respawnTimer:0,
        protection:this.player?.invulnerableTimer||0,
        playerRank,
        participantCount:scoreboard.length,
        playerStats,
        scoreboard,
        finished:matchFinished,
        winner:scoreboard.find((entry)=>entry.isWinner)||(matchFinished?scoreboard[0]:null),
      };
    }
    if(this.gameType!=='chase')return {type:'practice'};
    const scoreboard=this.vehicles.map((vehicle,index)=>({
      name:vehicle.displayName||`AUTO ${index+1}`,
      seconds:this.chaseScores.get(vehicle)||0,
      isPlayer:vehicle===this.player,
      isPursued:vehicle===this.chasePursued,
      color:vehicle.car.userData.bodyColor||vehicle.config.color||'#ffffff',
    })).sort((a,b)=>b.seconds-a.seconds);
    return {
      type:'chase',
      remaining:this.chaseRemaining,
      duration:this.chaseDuration,
      protection:this.chaseTransferCooldown,
      pursuedName:this.chasePursued?.displayName||'—',
      pursuedIsPlayer:this.chasePursued===this.player,
      targetInstability:(this.chasePursued?.chaseInstability||0)/GAME_MODE_RULES.chase.instabilityThreshold*100,
      playerSlowed:(this.player?.chaseSlowTimer||0)>0,
      playerNitroLocked:(this.player?.chaseNitroLockTimer||0)>0,
      scoreboard,
      finished:matchFinished,
      winner:scoreboard[0]||null,
    };
  }

  beginPostMatch(result=null){
    if(this.mode==='post-match'||this.mode==='results')return;
    this.mode='post-match';
    this.postMatchTimer=POST_MATCH_DURATION;
    this.input.clear();
    this.input.stopRumble();
    this.input.pauseMatchInput(true);
    this.inputTelemetry.finish(true);
    this.projectiles.clear();
    this.postMatchResult=result||this.getMatchState();
    this.vehicles.forEach((vehicle)=>{
      vehicle.velocity.set(0,0,0);
      vehicle.boosting=false;
      vehicle.throttle=0;
      vehicle.handbraking=false;
      resetVehiclePowerups(vehicle);
    });
    this.audio.update(0,false,false);
    this.audio.clearSpatialVehicles();
    this.healthBars.forEach((bar)=>{bar.visible=false;});
    this.callbacks.onPostMatch?.(this.postMatchResult,POST_MATCH_DURATION);
  }

  updatePostMatch(dt){
    if(this.mode!=='post-match')return;
    this.postMatchTimer=Math.max(0,this.postMatchTimer-dt);
    const orbitOffset=tempA.copy(this.camera.position).sub(this.cameraTarget);
    if(orbitOffset.lengthSq()>.01){
      orbitOffset.applyAxisAngle(CAMERA_UP,dt*.075);
      this.camera.position.copy(this.cameraTarget).add(orbitOffset);
      this.camera.lookAt(this.cameraTarget);
    }
    this.vehicles.forEach((vehicle)=>updateCarVisual(vehicle.car,vehicle,dt));
    this.effects.update(dt);
    this.audio.update(0,false,false);
    if(this.postMatchTimer>0)return;
    this.mode='results';
    this.input.clear();
    const result=this.postMatchResult||this.getMatchState();
    this.postMatchResult=result;
    this.callbacks.onMatchEnd?.(result);
  }

  finishChase() {
    if(this.mode!=='game')return;
    this.beginPostMatch();
  }

  finishBattle() {
    if(this.mode!=='game'||this.gameType!=='battle')return;
    this.battleWinner=this.battleScore.blue===this.battleScore.red?null:this.battleScore.blue>this.battleScore.red?'blue':'red';
    this.beginPostMatch();
  }

  finishFreeForAll(winner=null) {
    if(this.mode!=='game'||this.gameType!=='ffa')return;
    this.ffaWinner=winner||[...this.vehicles].sort((a,b)=>(b.combatStats?.kills||0)-(a.combatStats?.kills||0)
      ||(b.combatStats?.assists||0)-(a.combatStats?.assists||0)
      ||(a.combatStats?.deaths||0)-(b.combatStats?.deaths||0))[0]||null;
    this.beginPostMatch();
  }

  finishRupture(winner=null){
    if(this.mode!=='game'||this.gameType!=='rupture')return;
    this.ruptureWinner=winner||this.vehicles.find((vehicle)=>!vehicle.ruptureEliminated)||null;
    this.beginPostMatch();
  }

  handleImpact(force,position) {
    this.cameraShake=Math.min(.42,this.cameraShake+force*.018);
    this.effects.burst(position,'#ffd857',Math.min(12,Math.round(force)),Math.min(7,force*.55));
    this.audio.blip(100+Math.random()*30,.11,'square',Math.min(.13,force*.012));
    const intensity=THREE.MathUtils.clamp((force-2)/13,.12,1);
    this.input.rumble({
      duration:85+intensity*180,
      weakMagnitude:.2+intensity*.38,
      strongMagnitude:.18+intensity*.78,
      priority:2,
      key:'vehicle-impact',
      minInterval:90,
    });
  }

  handleLanding(airTime,force) {
    if(airTime>.65)this.callbacks.onStunt?.(airTime);
    if(force>2.4){
      const intensity=THREE.MathUtils.clamp((force-2.4)/12,.08,.82);
      this.input.rumble({
        duration:70+intensity*150,
        weakMagnitude:.12+intensity*.25,
        strongMagnitude:.16+intensity*.66,
        priority:1.4,
        key:'landing',
        minInterval:120,
      });
    }
    if(force>5){this.cameraShake=Math.min(.3,this.cameraShake+force*.012);this.effects.burst(this.player.car.position,'#e5c798',7,3.5);this.audio.blip(85,.14,'triangle',.1);}
  }

  handleAirDodge({directional=false}={}){
    this.cameraShake=Math.max(this.cameraShake,directional?.085:.045);
    this.audio.blip(directional?430:360,.14,'sawtooth',.075);
    this.input.rumble({
      duration:directional?145:105,
      weakMagnitude:directional?.38:.24,
      strongMagnitude:directional?.46:.28,
      priority:1.2,
      key:'air-dodge',
      minInterval:90,
    });
  }

  handleVehicleDestroyed(vehicle,position) {
    resetVehiclePowerups(vehicle);
    if(this.gameType==='battle'||this.gameType==='ffa')this.recordCombatElimination(vehicle);
    this.playVehicleDestroyedEffect(vehicle,position);
  }

  playVehicleDestroyedEffect(vehicle,position) {
    const loadout=vehicle.car.userData.loadout||this.defaultLoadout(vehicle.config.id);
    const explosion=getDeathExplosion(loadout.explosionId);
    this.effects.elimination(position,explosion);
    const markerPosition=position.clone();
    markerPosition.y=getGroundHeight(this.world,markerPosition);
    const tombstone=getTombstone(loadout.tombstoneId);
    this.effects.placeTombstone(markerPosition,tombstone,vehicle.car.userData.paintFinish);
    if(vehicle===this.player){
      this.audio.explosion(explosion,1);
      this.audio.tombstone(tombstone,.82);
    }else{
      this.audio.explosionAt(explosion,position,1);
      this.audio.tombstoneAt(tombstone,markerPosition,.82);
    }
    if(vehicle.isPlayer){
      this.cameraShake=.58;
      this.input.stopRumble();
      this.input.rumble({
        duration:560,weakMagnitude:.9,strongMagnitude:1,priority:4,key:'player-explosion',
      });
    }
  }

  recordCombatElimination(victim) {
    const isBattle=this.gameType==='battle';
    victim.combatStats.deaths+=1;
    const killer=victim.lastDamager&&victim.lastDamager!==victim
      &&(!isBattle||victim.lastDamager.teamId!==victim.teamId)?victim.lastDamager:null;
    const assists=[];
    if(killer){
      killer.combatStats.kills+=1;
      if(killer===this.player)this.inputTelemetry.elimination();
      if(isBattle)this.battleScore[killer.teamId]+=1;
      victim.damageContributors.forEach((contribution,attacker)=>{
        const eligibleAssist=attacker!==killer&&attacker!==victim&&contribution.remaining>0
          &&(!isBattle||attacker.teamId===killer.teamId);
        if(eligibleAssist){
          attacker.combatStats.assists+=1;
          assists.push(attacker);
        }
      });
    }
    this.callbacks.onBattleEvent?.({
      victimName:victim.displayName,
      victimIsPlayer:victim===this.player,
      killerName:killer?.displayName||'EL ESCENARIO',
      killerIsPlayer:killer===this.player,
      playerAssist:assists.includes(this.player),
      blueScore:this.battleScore.blue,
      redScore:this.battleScore.red,
    });
    if(!killer)return;
    if(isBattle&&this.battleScore[killer.teamId]>=this.battleKillLimit)this.finishBattle();
    else if(!isBattle&&this.ffaOvertime&&this.ffaOvertimeContenders.has(killer))this.finishFreeForAll(killer);
    else if(!isBattle&&killer.combatStats.kills>=this.ffaKillLimit)this.finishFreeForAll(killer);
  }

  playProjectileShot(vehicle,position=vehicle?.car?.position){
    const cannon=getCannon(vehicle.car.userData.cannonId);
    const projectile=getProjectile(vehicle.car.userData.projectileId||cannon.defaultProjectileId);
    if(vehicle===this.player){
      this.inputTelemetry.shot();
      this.audio.cannonFire(cannon,.9);
      this.audio.projectileTravel(projectile,.72);
      this.input.rumble({
        duration:72,weakMagnitude:.42,strongMagnitude:.14,priority:.8,key:'player-fire',minInterval:34,
      });
    }else{
      this.audio.cannonFireAt(cannon,position,.9);
      this.audio.projectileTravelAt(projectile,position,.72);
    }
  }

  playNetworkProjectileShot({position,owner,cannonId,projectileId}={}){
    if(!position||owner===this.player)return;
    const cannon=getCannon(cannonId||owner?.car?.userData?.cannonId);
    const projectile=getProjectile(
      projectileId||owner?.car?.userData?.projectileId||cannon.defaultProjectileId,
    );
    this.audio.cannonFireAt(cannon,position,.9);
    this.audio.projectileTravelAt(projectile,position,.72);
  }

  preloadVehicleAudio(vehicle){
    const userData=vehicle?.car?.userData;
    if(!userData)return;
    const loadout=userData.loadout||{};
    const cannon=getCannon(userData.cannonId||loadout.cannonId);
    const projectile=getProjectile(userData.projectileId||loadout.projectileId||cannon.defaultProjectileId);
    this.audio.prepareCannonSample(cannon);
    this.audio.prepareDesignSample(projectile,'travel');
    this.audio.prepareDesignSample(projectile,'impact');
    this.audio.prepareNitroSample(userData.nitroEffect);
    this.audio.prepareDesignSample(getDeathExplosion(loadout.explosionId),'sound');
    this.audio.prepareDesignSample(getTombstone(loadout.tombstoneId),'sound');
  }

  handleProjectileImpact(position,owner=null,projectileId=null,target=null,{absorbed=false}={}) {
    const projectile=getProjectile(projectileId||owner?.car?.userData?.projectileId);
    this.audio.projectileImpactAt(projectile,position,1);
    if(!absorbed&&this.mode!=='online-game'&&owner===this.player&&target&&target!==this.player){
      this.inputTelemetry.hit();
    }
    if(target===this.player)this.input.rumble({
      duration:absorbed?110:260,weakMagnitude:absorbed?.18:.62,strongMagnitude:absorbed?.25:.84,
      priority:3,key:absorbed?'projectile-block':'projectile-hit',minInterval:70,
    });
  }

  testPlayerElimination() {
    if(this.mode!=='game'||!this.player||this.gameType!=='practice')return false;
    // Si se prueba apenas aparece el auto, desplazamos únicamente el punto de
    // la eliminación para que la lápida no quede escondida bajo el respawn.
    if(this.player.car.position.distanceTo(this.player.spawn)<1.15){
      const forward=new THREE.Vector3(0,0,-1).applyEuler(this.player.car.rotation);
      this.player.car.position.addScaledVector(forward,1.7);
    }
    this.player.damage(Math.max(100,this.player.health));
    return true;
  }

  attachHealthBar(vehicle,teamColor=null) {
    const bar=createVehicleHealthBar(teamColor);
    bar.userData.vehicle=vehicle;
    this.scene.add(bar);
    this.healthBars.push(bar);
  }

  attachTeamRing(vehicle,color) {
    const ring=createTeamRing(vehicle,color);
    this.scene.add(ring);
    this.teamRings.push(ring);
  }

  updateHealthBars() {
    this.healthBars.forEach((bar)=>{
      const vehicle=bar.userData.vehicle;
      const rupture=this.gameType==='rupture'||this.onlineSession?.match.mode==='rupture';
      bar.visible=!vehicle.disabled
        &&(!rupture||(vehicle.ruptureInstability||0)>=30)
        &&(vehicle.invulnerableTimer<=0||Math.floor(this.gameElapsed*9)%2===0);
      if(vehicle.disabled)return;
      const ratio=THREE.MathUtils.clamp(
        rupture?(vehicle.ruptureInstability||0)/100:vehicle.health/100,
        0,
        1,
      );
      bar.position.copy(vehicle.car.position);
      bar.position.y+=vehicle.car.userData.collisionHeight+.28;
      bar.quaternion.copy(this.camera.quaternion);
      const fill=bar.userData.fill;
      fill.scale.x=Math.max(.001,ratio);
      fill.position.x=-.39*(1-ratio);
      fill.material.color.set(rupture
        ?ratio>.72?'#ff536d':ratio>.38?'#ffd34f':'#55e8ff'
        :ratio>.55?'#66ef8c':ratio>.25?'#ffd34f':'#ff536d');
    });
  }

  updateTeamRings() {
    this.teamRings.forEach((ring)=>{
      const vehicle=ring.userData.vehicle;
      ring.visible=!vehicle.disabled;
      if(!ring.visible)return;
      ring.position.copy(vehicle.car.position);
      ring.position.y+=.035;
      ring.material.opacity=vehicle===this.player ? .72 : .52;
    });
  }

  pause(value=true) {
    if(value&&this.mode==='game'){this.mode='paused';this.input.clear();this.input.stopRumble();this.input.pauseMatchInput(true);this.audio.pauseMusic(true);this.callbacks.onPause?.(true);}
    else if(!value&&this.mode==='paused'){this.mode='game';this.input.clear();this.input.pauseMatchInput(false);this.audio.pauseMusic(false);this.clock.getDelta();this.callbacks.onPause?.(false);}
  }

  pauseOnline(value=true) {
    if(this.mode!=='online-game')return;
    this.onlinePaused=Boolean(value);
    this.input.clear();
    if(this.onlinePaused)this.input.stopRumble();
    this.input.pauseMatchInput(this.onlinePaused);
    const session=this.onlineSession;
    if(session&&this.onlinePaused){
      session.queuedJump=false;
      session.queuedFireCount=0;
      session.queuedPowerup=false;
      session.heldControls=normalizeNetworkControls();
    }
    this.clock.getDelta();
    this.callbacks.onPause?.(this.onlinePaused,{online:true});
  }

  beginStartCountdown(duration=3.6){
    this.startCountdownRemaining=Math.max(0,Number(duration)||0);
    this.startCountdownLabel=null;
    if(this.startCountdownRemaining>.01)this.updateStartCountdown(0);
    else this.callbacks.onCountdown?.('',false);
  }

  updateStartCountdown(dt){
    if(this.startCountdownRemaining<=0)return false;
    this.startCountdownRemaining=Math.max(0,this.startCountdownRemaining-dt);
    if(this.startCountdownRemaining<=0){
      this.startCountdownLabel=null;
      this.callbacks.onCountdown?.('',false);
      return true;
    }
    const goDuration=.6;
    const label=this.startCountdownRemaining<=goDuration
      ?'¡YA!':String(Math.min(3,Math.max(1,Math.ceil(this.startCountdownRemaining-goDuration))));
    if(label!==this.startCountdownLabel){
      this.startCountdownLabel=label;
      this.callbacks.onCountdown?.(label,true);
      if(label==='¡YA!')this.audio.blip(680,.16,'square',.075);
      else this.audio.blip(330+(4-Number(label))*65,.085,'square',.055);
    }
    return true;
  }

  stopStartCountdown(){
    this.startCountdownRemaining=0;
    this.startCountdownLabel=null;
    this.callbacks.onCountdown?.('',false);
  }

  backToMenu() {
    this.inputTelemetry.finish(false);
    this.input.endMatchInput();
    this.clearVehicles();
    this.mode='menu';
    this.audio.pauseMusic(false);
    this.audio.playMenuMusic();
    this.setMap(this.selectedMapId);
    this.setCar(this.selectedCarId);
    this.callbacks.onMenu?.();
  }

  returnToOnlineLobby() {
    this.inputTelemetry.finish(false);
    this.input.endMatchInput();
    this.clearVehicles();
    this.input.clear();
    this.mode='online';
    this.gameType='online';
    this.audio.pauseMusic(false);
    this.audio.playMenuMusic();
    this.clock.getDelta();
  }

  restart() {
    const gameType=this.gameType;
    // El escenario ya está cargado. startMatch limpia la partida anterior y
    // reconstruye únicamente los vehículos; recrear también el mapa hacía que
    // el botón "Jugar de nuevo" bloqueara el hilo principal varios segundos.
    this.startMatch(gameType);
  }

  powerupsEnabled(){
    return (this.onlineSession?.match.mode||this.gameType)!=='tutorial';
  }

  initializePowerups(){
    if(this.ruptureArena){
      this.rupturePowerupDirector=new RupturePowerupDirector(this.ruptureArena);
      this.powerupCrates=this.rupturePowerupDirector.crates;
    }else{
      this.rupturePowerupDirector=null;
      this.powerupCrates=createPowerupCrates(this.world,{
        groundHeight:(point)=>getGroundHeight(this.world,point),
      });
    }
    this.syncPowerupCrateVisuals(this.powerupCrates);
  }

  createPowerupCrateVisual(crate){
    const group=new THREE.Group();
    group.name=`mystery-${crate.id}`;
    const shellMaterial=new THREE.MeshPhysicalMaterial({
      color:'#542591',emissive:'#321060',emissiveIntensity:1.25,metalness:.32,
      roughness:.23,clearcoat:1,clearcoatRoughness:.16,transparent:true,opacity:.88,
    });
    const shell=new THREE.Mesh(new RoundedBoxGeometry(.72,.72,.72,5,.075),shellMaterial);
    shell.castShadow=true;
    shell.receiveShadow=true;

    const coreMaterial=new THREE.MeshBasicMaterial({
      color:'#69f3ff',transparent:true,opacity:.74,depthWrite:false,
      blending:THREE.AdditiveBlending,
    });
    const core=new THREE.Mesh(new THREE.OctahedronGeometry(.29,1),coreMaterial);
    core.rotation.set(Math.PI/4,0,Math.PI/4);

    const frameMaterial=new THREE.MeshPhysicalMaterial({
      color:'#f6bf35',emissive:'#a85008',emissiveIntensity:.75,metalness:.72,
      roughness:.22,clearcoat:.8,
    });
    const frame=new THREE.Group();
    const addBar=(size,position)=>{
      const bar=new THREE.Mesh(new RoundedBoxGeometry(size.x,size.y,size.z,3,.022),frameMaterial);
      bar.position.copy(position);bar.castShadow=true;frame.add(bar);
    };
    for(const y of [-.37,.37])for(const z of [-.37,.37])addBar(
      new THREE.Vector3(.88,.075,.075),new THREE.Vector3(0,y,z),
    );
    for(const x of [-.37,.37])for(const z of [-.37,.37])addBar(
      new THREE.Vector3(.075,.88,.075),new THREE.Vector3(x,0,z),
    );
    for(const x of [-.37,.37])for(const y of [-.37,.37])addBar(
      new THREE.Vector3(.075,.075,.88),new THREE.Vector3(x,y,0),
    );
    for(const x of [-.39,.39])for(const y of [-.39,.39])for(const z of [-.39,.39]){
      const cap=new THREE.Mesh(new RoundedBoxGeometry(.145,.145,.145,3,.035),frameMaterial);
      cap.position.set(x,y,z);cap.castShadow=true;frame.add(cap);
    }

    const canvas=document.createElement('canvas');
    canvas.width=256;canvas.height=256;
    const context=canvas.getContext('2d');
    const gradient=context.createRadialGradient(128,128,12,128,128,118);
    gradient.addColorStop(0,'rgba(255,247,148,.98)');
    gradient.addColorStop(.42,'rgba(117,238,255,.94)');
    gradient.addColorStop(1,'rgba(63,23,118,.15)');
    context.fillStyle=gradient;context.beginPath();context.arc(128,128,116,0,Math.PI*2);context.fill();
    context.strokeStyle='#fff3a0';context.lineWidth=12;context.beginPath();context.arc(128,128,103,0,Math.PI*2);context.stroke();
    context.fillStyle='#32134f';context.strokeStyle='#ffffff';context.lineWidth=8;
    context.font='1000 176px Nunito, sans-serif';context.textAlign='center';context.textBaseline='middle';
    context.strokeText('?',128,139);context.fillText('?',128,139);
    const symbolTexture=new THREE.CanvasTexture(canvas);
    symbolTexture.colorSpace=THREE.SRGBColorSpace;
    const panelMaterial=new THREE.MeshBasicMaterial({
      map:symbolTexture,transparent:true,depthWrite:false,side:THREE.DoubleSide,
    });
    const panels=new THREE.Group();
    const addPanel=(position,rotation)=>{
      const panel=new THREE.Mesh(new THREE.PlaneGeometry(.43,.43),panelMaterial);
      panel.position.copy(position);panel.rotation.copy(rotation);panels.add(panel);
    };
    addPanel(new THREE.Vector3(0,0,.414),new THREE.Euler(0,0,0));
    addPanel(new THREE.Vector3(0,0,-.414),new THREE.Euler(0,Math.PI,0));
    addPanel(new THREE.Vector3(.414,0,0),new THREE.Euler(0,Math.PI/2,0));
    addPanel(new THREE.Vector3(-.414,0,0),new THREE.Euler(0,-Math.PI/2,0));

    const haloMaterial=new THREE.MeshBasicMaterial({
      color:'#6eefff',transparent:true,opacity:.82,depthWrite:false,blending:THREE.AdditiveBlending,
    });
    const haloOuter=new THREE.Mesh(new THREE.TorusGeometry(.59,.032,8,36),haloMaterial);
    const haloInner=new THREE.Mesh(
      new THREE.TorusGeometry(.43,.018,7,32),
      new THREE.MeshBasicMaterial({
        color:'#fff16a',transparent:true,opacity:.72,depthWrite:false,blending:THREE.AdditiveBlending,
      }),
    );
    [haloOuter,haloInner].forEach((halo)=>{halo.rotation.x=Math.PI/2;halo.position.y=-.48;});
    group.add(core,shell,frame,panels,haloOuter,haloInner);
    group.position.set(crate.x,crate.y,crate.z);
    group.userData={
      wasAvailable:false,baseY:crate.y,spawnAnimation:0,spinSpeed:.75,
      core,shellMaterial,frameMaterial,panelMaterial,haloOuter,haloInner,
    };
    this.scene.add(group);
    return group;
  }

  syncPowerupCrateVisuals(crates=[]){
    const ids=new Set();
    crates.forEach((crate)=>{
      ids.add(crate.id);
      let visual=this.powerupCrateVisuals.get(crate.id);
      if(!visual){visual=this.createPowerupCrateVisual(crate);this.powerupCrateVisuals.set(crate.id,visual);}
      visual.position.set(crate.x,crate.y,crate.z);
      visual.userData.baseY=crate.y;
      const appeared=Boolean(crate.available)&&!visual.userData.wasAvailable;
      visual.visible=Boolean(crate.available);
      visual.userData.wasAvailable=Boolean(crate.available);
      if(appeared){
        visual.userData.spawnAnimation=0;
        visual.scale.setScalar(.12);
        this.effects.powerupCrateAppear(visual.position);
      }
    });
    this.powerupCrateVisuals.forEach((visual,id)=>{
      if(ids.has(id))return;
      this.scene.remove(visual);
      visual.traverse((child)=>{
        child.geometry?.dispose?.();child.material?.map?.dispose?.();child.material?.dispose?.();
      });
      this.powerupCrateVisuals.delete(id);
    });
  }

  updatePowerupVisuals(dt){
    this.powerupCrateVisuals.forEach((visual)=>{
      if(!visual.visible)return;
      const data=visual.userData;
      const distance=this.player?.car.position.distanceTo(visual.position)??Infinity;
      const proximity=1-THREE.MathUtils.clamp((distance-1.2)/5.5,0,1);
      data.spinSpeed=THREE.MathUtils.lerp(data.spinSpeed,.72+proximity*2.35,1-Math.exp(-5*dt));
      visual.rotation.y+=dt*data.spinSpeed;
      visual.rotation.z=Math.sin(this.elapsed*1.7+visual.position.x*.13)*.035;
      visual.position.y=data.baseY+Math.sin(this.elapsed*2.65+visual.position.z*.17)*(.055+proximity*.025);
      data.core.rotation.y-=dt*(1.8+proximity*2.5);
      data.core.rotation.x+=dt*.85;
      data.core.scale.setScalar(.88+Math.sin(this.elapsed*5.5)*.12+proximity*.14);
      data.haloOuter.rotation.z+=dt*(.7+proximity*1.4);
      data.haloInner.rotation.z-=dt*(1.15+proximity*1.8);
      data.shellMaterial.emissiveIntensity=1.15+Math.sin(this.elapsed*4)*.2+proximity*.8;
      data.frameMaterial.emissiveIntensity=.65+proximity*.8;
      data.panelMaterial.opacity=.82+Math.sin(this.elapsed*6.5)*.14;
      data.spawnAnimation=Math.min(1,data.spawnAnimation+dt*2.8);
      const spawnScale=1-Math.pow(1-data.spawnAnimation,3);
      const targetScale=1.02+proximity*.075;
      visual.scale.setScalar(Math.max(.12,spawnScale*targetScale));
    });
    this.vehicles.forEach((vehicle)=>{
      if(!vehicle.powerupBubble){
        const radius=Math.max(.86,(vehicle.car.userData.collisionWidth||.6)*1.72);
        const bubble=new THREE.Mesh(new THREE.SphereGeometry(radius,24,16),new THREE.MeshBasicMaterial({
          color:'#63efff',transparent:true,opacity:.18,depthWrite:false,
          blending:THREE.AdditiveBlending,side:THREE.DoubleSide,
        }));
        bubble.position.y=(vehicle.car.userData.collisionHeight||.82)*.52;
        bubble.scale.set(1.08,1.08,1.48);
        bubble.visible=false;
        vehicle.car.add(bubble);
        vehicle.powerupBubble=bubble;
      }
      const timer=vehicle.powerupShieldTimer||0;
      vehicle.powerupBubble.visible=timer>0&&!vehicle.disabled;
      if(timer>0){
        const flicker=timer<1&&Math.sin(this.elapsed*32)<-.25;
        vehicle.powerupBubble.material.opacity=flicker?.07:.18+Math.sin(this.elapsed*5)*.035;
        vehicle.powerupBubble.rotation.y+=dt*.55;
      }
    });
  }

  powerupLineOfSight(owner,target){
    const start=owner.car.position.clone();
    const end=target.car.position.clone();
    start.y+=(owner.car.userData.collisionHeight||.82)*.55;
    end.y+=(target.car.userData.collisionHeight||.82)*.55;
    return !this.world.obstacles.some((obstacle)=>projectileObstacleIntersection(start,end,obstacle,.05));
  }

  selectHomingTarget(owner,current=null){
    if(this.onlineSession&&owner===this.player){
      const targetId=this.onlineSession.matchState?.powerupTargets
        ?.find((entry)=>entry.playerId===this.onlineSession.playerId)?.targetId;
      return targetId?this.onlineVehicles.get(targetId)?.vehicle||null:null;
    }
    const mode=this.onlineSession?.match.mode||this.gameType;
    const pursued=mode==='chase'
      ?this.onlineSession
        ?this.onlineVehicles.get(this.onlineSession.matchState?.pursuedId)?.vehicle
        :this.chasePursued
      :null;
    const candidates=mode==='chase'&&owner!==pursued&&pursued?[pursued]:this.vehicles;
    return powerupTarget(owner,candidates,owner.aimDirection,
      (source,target)=>this.powerupLineOfSight(source,target),current);
  }

  activatePowerup(vehicle){
    const activated=activateHeldPowerup(vehicle);
    if(!activated)return false;
    const definition=POWERUP_DEFINITIONS[activated];
    this.effects.burst(vehicle.car.position.clone().add(new THREE.Vector3(0,.35,0)),definition.color,16,5.2);
    if(activated===POWERUP_SHOCKWAVE)this.activateShockwave(vehicle);
    if(vehicle===this.player){
      this.audio.blip(activated===POWERUP_SHIELD?620:activated===POWERUP_HOMING?760:420,.2,'square',.1);
      if(activated===POWERUP_SHOCKWAVE){
        this.audio.blip(92,.42,'sawtooth',.13);
        this.input.rumble({duration:420,weakMagnitude:.72,strongMagnitude:.95,
          priority:4,key:'powerup-shockwave'});
      }
    }
    return true;
  }

  activateShockwave(owner){
    const origin=owner.car.position.clone();
    this.effects.shockwave(origin.clone().add(new THREE.Vector3(0,.18,0)),{
      shockwaveColor:'#cf8cff',shockwaveSize:POWERUP_SHOCKWAVE_RADIUS/1.4,
    });
    const playerDistance=this.player?.car.position.distanceTo(origin)||0;
    if(playerDistance<POWERUP_SHOCKWAVE_RADIUS*1.25){
      this.cameraShake=Math.max(this.cameraShake,.52*(1-playerDistance/(POWERUP_SHOCKWAVE_RADIUS*1.4)));
    }
    this.vehicles.forEach((vehicle)=>{
      if(vehicle===owner||vehicle.disabled||(owner.teamId&&vehicle.teamId===owner.teamId))return;
      applyShockwaveImpulse(vehicle,origin);
    });
    for(let index=this.projectiles.projectiles.length-1;index>=0;index-=1){
      const projectile=this.projectiles.projectiles[index];
      const away=projectile.position.clone().sub(origin);
      const distance=away.length();
      if(distance>POWERUP_SHOCKWAVE_RADIUS)continue;
      if(distance<2.1&&projectile.guided){
        this.scene.remove(projectile.root);
        this.projectiles.projectiles.splice(index,1);
        continue;
      }
      if(away.lengthSq()<.001)away.set(0,1,0);
      projectile.velocity.copy(away.normalize()).multiplyScalar(Math.max(12,projectile.velocity.length()));
      projectile.guided=false;projectile.homingTarget=null;projectile.homingTargetId=null;
    }
  }

  updatePowerups(dt,controls=null){
    if(!this.powerupsEnabled())return;
    if(this.rupturePowerupDirector)this.rupturePowerupDirector.update();
    else updatePowerupCrates(this.powerupCrates,dt);
    this.vehicles.forEach((vehicle)=>{
      if(vehicle.disabled||vehicle.powerupHeld||vehicle.powerupPending)return;
      const crate=this.powerupCrates.find((entry)=>entry.available
        &&(vehicle.car.position.x-entry.x)**2+(vehicle.car.position.y-entry.y)**2
          +(vehicle.car.position.z-entry.z)**2<=1.15**2);
      if(crate&&collectPowerup(vehicle,randomPowerup(crate.lastPowerup),crate)){
        this.effects.powerupCrateOpen(new THREE.Vector3(crate.x,crate.y,crate.z));
        if(vehicle===this.player)this.audio.blip(540,.12,'triangle',.08);
      }
    });
    if(controls?.powerup)this.activatePowerup(this.player);
    this.bots.forEach((brain)=>{
      const vehicle=brain.vehicle;
      if(!vehicle.powerupHeld||vehicle.disabled)return;
      const nearby=this.vehicles.some((candidate)=>candidate!==vehicle&&!candidate.disabled
        &&(!vehicle.teamId||candidate.teamId!==vehicle.teamId)
        &&candidate.car.position.distanceToSquared(vehicle.car.position)<55);
      if((vehicle.powerupHeld===POWERUP_NITRO&&vehicle.nitro<60)
        ||(vehicle.powerupHeld!==POWERUP_NITRO&&nearby))this.activatePowerup(vehicle);
    });
    this.homingTarget=this.player?.homingAmmoTimer>0
      ?this.selectHomingTarget(this.player,this.homingTarget):null;
    this.syncPowerupCrateVisuals(this.powerupCrates);
    this.updatePowerupVisuals(dt);
  }

  clearVehicles() {
    this.stopStartCountdown();
    this.input.stopRumble();
    this.audio.clearSpatialVehicles();
    this.projectiles.clear();this.effects.clear();
    this.syncPowerupCrateVisuals([]);
    this.ruptureArena?.dispose();
    this.ruptureArena=null;
    this.vehicles.forEach((vehicle)=>this.scene.remove(vehicle.car));
    this.healthBars.forEach((bar)=>{
      this.scene.remove(bar);
      bar.traverse((child)=>{if(child.isMesh){child.geometry.dispose();child.material.dispose();}});
    });
    this.teamRings.forEach((ring)=>{
      this.scene.remove(ring);
      ring.geometry.dispose();
      ring.material.dispose();
    });
    if(this.chaseMarker){
      this.scene.remove(this.chaseMarker);
      this.chaseMarker.traverse((child)=>{if(child.isMesh){child.geometry.dispose();child.material.dispose();}});
    }
    if(this.showcaseCar)this.scene.remove(this.showcaseCar);
    this.vehicles=[];this.bots=[];this.healthBars=[];this.teamRings=[];this.player=null;this.showcaseCar=null;
    this.powerupCrates=[];this.rupturePowerupDirector=null;this.homingTarget=null;
    this.cameraFollowedVehicle=null;
    this.onlineSession=null;this.onlinePaused=false;this.onlineVehicles=new Map();
    this.chaseMarker=null;this.chasePursued=null;this.chaseScores=new Map();this.chaseLastPair=null;this.navigator=null;
    this.battleScore={blue:0,red:0};this.battleWinner=null;
    this.ffaWinner=null;this.ffaOvertime=false;this.ffaOvertimeContenders=new Set();
    this.ruptureWinner=null;this.ruptureFinishTimer=0;
    this.tutorial=null;
    this.postMatchTimer=0;this.postMatchResult=null;
    this.resetCombatAim();
  }

  updateGame(dt) {
    const controls=this.updateCombatAim(this.input.snapshot(),dt);
    if(controls.pause){this.pause(true);this.input.endFrame();return;}
    if(this.updateStartCountdown(dt)){
      this.updateChaseCamera(dt);
      if(this.gameType==='practice'||this.gameType==='battle'||this.gameType==='ffa'||this.gameType==='rupture')this.updateHealthBars();
      if(this.gameType==='battle')this.updateTeamRings();
      this.updatePlayerVehicleAudio();
      this.callbacks.onHud?.(this.player,this.getMatchState());
      this.input.endFrame();
      return;
    }
    this.gameElapsed+=dt;
    if(this.gameType==='chase')this.updateChaseCombatState(dt);
    if(controls.powerup)this.activatePowerup(this.player);
    const playerControls=this.applyChaseControlEffects(this.player,controls);
    this.player.update(dt,playerControls);
    this.applyVehicleAimControls(this.player,playerControls,dt);
    if(this.gameType==='practice'&&controls.testKo)this.testPlayerElimination();
    if(!this.player.disabled
      &&(this.gameType==='rupture'||this.player.collisionGhostTimer<=0)
      &&controls.fire
      &&this.projectiles.fire(this.player,this.player.aimDirection,{
        homingTarget:this.player.homingAmmoTimer>0?this.selectHomingTarget(this.player,this.homingTarget):null,
      })
    )this.playProjectileShot(this.player);
    this.bots.forEach((brain)=>{
      if(brain.vehicle.disabled){brain.vehicle.update(dt,{});return;}
      const modeState=this.gameType==='chase'?{type:'chase',pursued:this.chasePursued}
        :this.gameType==='battle'?{type:'battle'}
          :this.gameType==='ffa'?{type:'ffa'}:null;
      const rawBotControls=this.gameType==='rupture'
        ?this.ruptureBotControls(brain,dt)
        :brain.update(dt,this.player,this.vehicles,modeState);
      const botControls=this.applyChaseControlEffects(brain.vehicle,rawBotControls);
      brain.vehicle.update(dt,botControls);
      this.applyVehicleAimControls(brain.vehicle,botControls,dt);
      if(botControls.fire
        &&(this.gameType==='rupture'||brain.vehicle.collisionGhostTimer<=0)
        &&this.projectiles.fire(brain.vehicle,brain.vehicle.aimDirection,{
          homingTarget:brain.vehicle.homingAmmoTimer>0?this.selectHomingTarget(brain.vehicle):null,
        })
      )this.playProjectileShot(brain.vehicle);
    });
    this.updatePowerups(dt);
    const contacts=resolveVehicleContacts(this.vehicles);
    if(this.gameType==='chase'){
      this.handleChaseContacts(contacts);
      this.projectiles.update(dt,this.vehicles,this.world,(hit)=>this.handleChaseProjectileHit(hit));
    }else if(this.gameType==='rupture'){
      this.handleRuptureContacts(contacts);
      this.projectiles.update(dt,this.vehicles,this.world,(hit)=>this.handleRuptureProjectileHit(hit));
    }else this.projectiles.update(dt,this.vehicles,this.world);
    this.updateChaseMode(dt);
    this.updateBattleMode(dt);
    this.updateFreeForAllMode(dt);
    this.updateRuptureMode(dt);
    this.updateEffectsForPlayer(dt);
    this.effects.update(dt);
    this.updateChaseCamera(dt);
    this.updateTutorial(dt,controls);
    if(this.gameType==='practice'||this.gameType==='battle'||this.gameType==='ffa'||this.gameType==='rupture')this.updateHealthBars();
    if(this.gameType==='battle')this.updateTeamRings();
    this.updatePlayerVehicleAudio();
    this.callbacks.onHud?.(this.player,this.getMatchState());
    this.input.endFrame();
  }

  updatePlayerVehicleAudio() {
    if(!this.player){
      this.audio.update(0,false,false);
      this.audio.clearSpatialVehicles();
      return;
    }
    const active=!this.player.disabled;
    this.audio.update(
      active?this.player.forwardSpeed:0,
      active&&this.player.boosting,
      active,
      active?this.player.skidAmount:0,
      this.player.car.userData.nitroEffect,
      this.player.car.userData.engineSound,
    );
    this.audio.updateSpatialVehicles(this.camera,this.player,this.vehicles);
  }

  updateEffectsForPlayer(dt) {
    this.dustClock-=dt;this.boostClock-=dt;
    const speed=Math.abs(this.player.forwardSpeed);
    if(this.player.wheelsGrounded&&speed>4&&(Math.abs(this.player.lateralSpeed)>2.3||this.player.throttle!==0)&&this.dustClock<=0){
      this.dustClock=.045;
      const behind=new THREE.Vector3(0,.03,this.player.car.userData.length*.58).applyEuler(this.player.car.rotation).add(this.player.car.position);
      const dustColor=this.world.id==='garden'?'#b18d59':this.world.id==='bedroom'?'#d9b267':'#c9996d';
      this.effects.dust(behind,this.player.velocity,dustColor);
    }
    if(this.player.boosting&&this.boostClock<=0){
      const nitro=this.player.car.userData.nitroEffect;
      const particleLife=Number(nitro.trailLength??1.55)/Math.max(.1,Number(nitro.speed??3));
      this.boostClock=THREE.MathUtils.clamp(
        particleLife/Math.max(1,Number(nitro.particleCount??30)),
        .008,
        .08,
      );
      this.effects.vehicleBoostSpark(this.player.car);
    }
    if(this.player.boosting){
      const speedRatio=THREE.MathUtils.clamp(Math.abs(this.player.forwardSpeed)/this.player.config.maxSpeed,0,1.4);
      this.input.rumble({
        duration:135,
        weakMagnitude:.2+speedRatio*.16,
        strongMagnitude:.07+speedRatio*.08,
        priority:.35,
        key:'nitro-loop',
        minInterval:82,
      });
    }else if(this.player.wheelsGrounded&&this.player.skidAmount>.18){
      const skid=THREE.MathUtils.clamp(this.player.skidAmount,0,1);
      this.input.rumble({
        duration:105,
        weakMagnitude:.1+skid*.22,
        strongMagnitude:.08+skid*.18,
        priority:.25,
        key:'skid-loop',
        minInterval:78,
      });
    }
  }

  resetCombatAim(){
    this.combatAimTarget=null;
    this.combatAimAssistTarget=null;
    this.combatAimYaw=0;
    this.combatAimPitch=0;
    this.combatCameraAimYaw=0;
    this.combatCameraAimPitch=0;
    this.combatAimIdleTime=0;
    this.combatPrecisionAim=false;
    this.combatLookX=0;
    this.combatLookY=0;
    this.combatAimInput.x=0;
    this.combatAimInput.y=0;
    this.combatCameraReverse=false;
    this.combatAimDirection.set(0,0,-1);
    this.combatCameraAimDirection.set(0,0,-1);
    if(this.player){
      if(!this.player.aimDirection)this.player.aimDirection=new THREE.Vector3();
      weaponAimDirection(this.player,0,0,this.player.aimDirection);
      this.combatAimDirection.copy(this.player.aimDirection);
    }
  }

  combatAimCandidates(){
    if(!this.player)return [];
    const player=this.player;
    const forward=weaponAimDirection(player,0,0,new THREE.Vector3());
    const origin=player.car.position;
    const pursued=this.gameType==='online'
      ?this.onlineVehicles.get(this.onlineSession?.matchState?.pursuedId)?.vehicle
      :this.chasePursued;
    return this.vehicles.filter((vehicle)=>{
      if(vehicle===player||vehicle.disabled)return false;
      if((this.gameType==='battle'||this.onlineSession?.match.mode==='battle')
        &&vehicle.teamId===player.teamId)return false;
      const chaseMode=this.gameType==='chase'||this.onlineSession?.match.mode==='chase';
      if(chaseMode&&pursued&&player!==pursued&&vehicle!==pursued)return false;
      const direction=vehicle.car.position.clone().sub(origin);
      const distance=direction.length();
      return distance>.1&&distance<52&&direction.normalize().dot(forward)>Math.cos(THREE.MathUtils.degToRad(86));
    }).map((vehicle)=>{
      const direction=vehicle.car.position.clone().sub(origin).normalize();
      return {
        vehicle,
        score:(1-direction.dot(forward))*28+origin.distanceTo(vehicle.car.position)*.025,
      };
    }).sort((first,second)=>first.score-second.score
      ||String(first.vehicle.displayName||'').localeCompare(String(second.vehicle.displayName||'')));
  }

  cycleCombatAimTarget(){
    const candidates=this.combatAimCandidates();
    if(!candidates.length){
      this.combatAimTarget=null;
      return;
    }
    const currentIndex=candidates.findIndex(({vehicle})=>vehicle===this.combatAimTarget);
    if(currentIndex<0){
      this.combatAimTarget=[...candidates].sort((first,second)=>{
        const firstDirection=first.vehicle.car.position.clone().sub(this.player.car.position).normalize();
        const secondDirection=second.vehicle.car.position.clone().sub(this.player.car.position).normalize();
        return secondDirection.dot(this.combatAimDirection)-firstDirection.dot(this.combatAimDirection)
          ||first.score-second.score;
      })[0].vehicle;
    }else this.combatAimTarget=candidates[currentIndex+1]?.vehicle||null;
    this.input.rumble({
      duration:this.combatAimTarget?72:42,
      weakMagnitude:this.combatAimTarget?.18:.08,
      strongMagnitude:this.combatAimTarget?.08:.03,
      priority:.45,
      key:'target-lock',
      minInterval:40,
    });
  }

  combatAimPoint(target=this.combatAimTarget){
    if(!target)return null;
    const collisionOffset=new THREE.Vector3(
      Number(target.car.userData.collisionOffsetX)||0,
      Math.max(.16,Number(target.car.userData.collisionOffsetY)||.34),
      Number(target.car.userData.collisionOffsetZ)||0,
    ).applyQuaternion(target.orientation||target.car.quaternion);
    const currentPoint=target.car.position.clone().add(collisionOffset);
    const relativeCompensation=this.player.velocity.clone()
      .multiplyScalar(.45)
      .sub(target.velocity);
    const leadTime=THREE.MathUtils.clamp(
      projectileInterceptTime(
        currentPoint.clone().sub(this.player.car.position),
        relativeCompensation,
        STANDARD_CANNON_PERFORMANCE.projectileSpeed,
      ),
      .04,
      STANDARD_CANNON_PERFORMANCE.projectileLife*.92,
    );
    return target.car.position.clone()
      .addScaledVector(target.velocity,leadTime)
      .add(collisionOffset);
  }

  combatAimTargetState(target){
    if(!target)return null;
    const collisionOffset=new THREE.Vector3(
      Number(target.car.userData.collisionOffsetX)||0,
      Math.max(.16,Number(target.car.userData.collisionOffsetY)||.34),
      Number(target.car.userData.collisionOffsetZ)||0,
    ).applyQuaternion(target.orientation||target.car.quaternion);
    const predictedPosition=this.combatAimPoint(target).sub(collisionOffset);
    const orientation=target.orientation||target.car.quaternion;
    return {
      x:predictedPosition.x,
      y:predictedPosition.y,
      z:predictedPosition.z,
      yaw:target.yaw,
      qx:orientation.x,
      qy:orientation.y,
      qz:orientation.z,
      qw:orientation.w,
      grounded:target.grounded,
      groundHeight:target.groundHeight,
    };
  }

  targetableCombatVehicles(){
    if(!this.player)return [];
    const pursued=this.gameType==='online'
      ?this.onlineVehicles.get(this.onlineSession?.matchState?.pursuedId)?.vehicle
      :this.chasePursued;
    return this.vehicles.filter((vehicle)=>{
      if(vehicle===this.player||vehicle.disabled)return false;
      if((this.gameType==='battle'||this.onlineSession?.match.mode==='battle')
        &&vehicle.teamId===this.player.teamId)return false;
      const chaseMode=this.gameType==='chase'||this.onlineSession?.match.mode==='chase';
      return !(chaseMode&&pursued&&this.player!==pursued&&vehicle!==pursued);
    });
  }

  cameraConvergencePoint(direction,predictTarget=true){
    const reference=this.player.car.position.clone().addScaledVector(direction,18);
    const ndc=reference.project(this.camera);
    const origin=this.camera.getWorldPosition(new THREE.Vector3());
    const rayDirection=new THREE.Vector3(ndc.x,ndc.y,.5)
      .unproject(this.camera)
      .sub(origin)
      .normalize();
    const end=origin.clone().addScaledVector(rayDirection,STANDARD_CANNON_PERFORMANCE.projectileSpeed*STANDARD_CANNON_PERFORMANCE.projectileLife);
    let nearest=null;
    for(const vehicle of this.targetableCombatVehicles()){
      const intersection=projectileVehicleIntersection(origin,end,vehicle,0);
      if(intersection&&(!nearest||intersection.time<nearest.time))nearest={...intersection,vehicle};
    }
    for(const obstacle of this.world?.obstacles||[]){
      const intersection=projectileObstacleIntersection(origin,end,obstacle,0);
      if(intersection&&(!nearest||intersection.time<nearest.time))nearest={...intersection,obstacle};
    }
    if(nearest?.vehicle)return {
      point:predictTarget?this.combatAimPoint(nearest.vehicle):nearest.position,
      target:predictTarget?nearest.vehicle:null,
    };
    return {point:nearest?.position||end,target:null};
  }

  assistedAimTarget(direction,{precision=false}={}){
    if(!this.player)return null;
    if(!this.aimSettings.controllerAimAssist)return null;
    const strength=this.aimSettings.controllerAimAssistStrength;
    const maxAngle=THREE.MathUtils.degToRad(
      precision
        ?2.8+strength*2.2
        :5.5+strength*5.5,
    );
    let best=null;
    for(const target of this.targetableCombatVehicles()){
      const point=this.combatAimPoint(target);
      const targetDirection=point.clone().sub(this.player.car.position).normalize();
      const angle=Math.acos(THREE.MathUtils.clamp(direction.dot(targetDirection),-1,1));
      if(angle>maxAngle)continue;
      const distance=this.player.car.position.distanceTo(point);
      const stickyBonus=target===this.combatAimAssistTarget?.18:0;
      const score=angle/maxAngle+distance*.0018-stickyBonus;
      if(best&&score>=best.score)continue;
      best={target,point,angle,score};
    }
    if(!best)return null;
    return best;
  }

  combatProjectileTrajectory(){
    const radius=STANDARD_CANNON_PERFORMANCE.projectileRadius;
    const launch=projectileLaunchState(this.player,radius,this.combatAimDirection);
    const velocity=launch.forward.clone()
      .multiplyScalar(STANDARD_CANNON_PERFORMANCE.projectileSpeed)
      .addScaledVector(this.player.velocity,.45);
    const end=launch.position.clone().addScaledVector(velocity,STANDARD_CANNON_PERFORMANCE.projectileLife);
    let nearest=null;
    const assistedTarget=this.combatAimTarget||this.combatAimAssistTarget;
    for(const vehicle of this.targetableCombatVehicles()){
      const intersection=projectileVehicleIntersection(launch.position,end,vehicle,radius,{
        launchGrounded:launch.launchGrounded,
        launchSurfaceY:launch.launchSurfaceY,
        targetState:vehicle===assistedTarget?this.combatAimTargetState(vehicle):null,
      });
      if(intersection&&(!nearest||intersection.time<nearest.time))nearest={...intersection,vehicle};
    }
    for(const obstacle of this.world?.obstacles||[]){
      const intersection=projectileObstacleIntersection(launch.position,end,obstacle,radius);
      if(intersection&&(!nearest||intersection.time<nearest.time))nearest={...intersection,obstacle};
    }
    return {
      point:nearest?.position||end,
      target:nearest?.vehicle||null,
      blocked:Boolean(nearest?.obstacle),
    };
  }

  updateCombatAim(controls,dt=0){
    if(!this.player)return controls;
    const usingMouse=controls.inputDevice==='keyboard-mouse';
    const frameDt=Math.max(1/240,Number(dt)||1/60);
    const shapedInput=usingMouse
      ?Object.assign(this.combatAimInput,{
        x:THREE.MathUtils.clamp(Number(controls.lookX)||0,-1,1),
        y:THREE.MathUtils.clamp(Number(controls.lookY)||0,-1,1),
      })
      :shapeWeaponAimStick(controls.lookX,controls.lookY,this.combatAimInput);
    // Stick y RMB gobiernan la cámara en ambos ejes. El cañón comparte sólo
    // el yaw: nunca sube ni baja, y vuelve al frente al soltar el control.
    this.combatPrecisionAim=false;
    const inputRadius=Math.hypot(shapedInput.x,shapedInput.y);
    const targetYaw=shapedInput.x*WEAPON_AIM_MAX_YAW;
    const targetPitch=shapedInput.y<0
      ?-shapedInput.y*WEAPON_AIM_MAX_PITCH_UP
      :-shapedInput.y*WEAPON_AIM_MAX_PITCH_DOWN;
    const responseRate=usingMouse?22:8+this.aimSettings.controllerSensitivity*18;
    const responseBlend=1-Math.exp(-frameDt*responseRate);
    this.combatCameraAimYaw=THREE.MathUtils.lerp(
      this.combatCameraAimYaw,
      targetYaw,
      responseBlend,
    );
    this.combatCameraAimPitch=THREE.MathUtils.lerp(
      this.combatCameraAimPitch,
      targetPitch,
      responseBlend,
    );
    this.combatAimIdleTime=inputRadius>.012?0:this.combatAimIdleTime+frameDt;
    this.combatCameraAimYaw=THREE.MathUtils.clamp(
      this.combatCameraAimYaw,
      -WEAPON_AIM_MAX_YAW,
      WEAPON_AIM_MAX_YAW,
    );
    this.combatCameraAimPitch=THREE.MathUtils.clamp(
      this.combatCameraAimPitch,
      -WEAPON_AIM_MAX_PITCH_DOWN,
      WEAPON_AIM_MAX_PITCH_UP,
    );
    const displayYaw=WEAPON_AIM_MAX_YAW>0?this.combatCameraAimYaw/WEAPON_AIM_MAX_YAW:0;
    const displayPitch=this.combatCameraAimPitch>=0
      ?this.combatCameraAimPitch/WEAPON_AIM_MAX_PITCH_UP
      :this.combatCameraAimPitch/WEAPON_AIM_MAX_PITCH_DOWN;
    const displayBlend=1-Math.exp(-frameDt*16);
    this.combatLookX=THREE.MathUtils.lerp(this.combatLookX,displayYaw,displayBlend);
    this.combatLookY=THREE.MathUtils.lerp(this.combatLookY,-displayPitch,displayBlend);
    this.combatCameraReverse=Boolean(controls.cameraReverse);
    this.combatAimTarget=null;
    this.combatAimAssistTarget=null;

    weaponAimDirection(
      this.player,
      this.combatCameraAimYaw,
      this.combatCameraAimPitch,
      this.combatCameraAimDirection,
    );
    this.combatAimYaw=this.combatCameraAimYaw;
    this.combatAimPitch=0;
    const desiredDirection=weaponAimDirection(
      this.player,
      this.combatAimYaw,
      0,
      new THREE.Vector3(),
    );
    return {
      ...controls,
      aimX:desiredDirection.x,
      aimY:desiredDirection.y,
      aimZ:desiredDirection.z,
    };
  }

  applyVehicleAimControls(vehicle,controls={},dt=1/60){
    if(!vehicle.aimDirection)weaponAimDirection(vehicle,0,0,vehicle.aimDirection=new THREE.Vector3());
    const desired=clampWeaponAimDirection(vehicle,controls,new THREE.Vector3());
    slewWeaponAimDirection(vehicle,vehicle.aimDirection,desired,dt,vehicle.aimDirection);
    if(vehicle===this.player)this.combatAimDirection.copy(vehicle.aimDirection);
    return vehicle.aimDirection;
  }

  setAimSettings({controllerSensitivity=50,controllerAimAssist=true,controllerAimAssistStrength=45}={}){
    this.aimSettings.controllerSensitivity=THREE.MathUtils.clamp((Number(controllerSensitivity)||0)/100,0,1);
    this.aimSettings.controllerAimAssist=Boolean(controllerAimAssist);
    this.aimSettings.controllerAimAssistStrength=THREE.MathUtils.clamp((Number(controllerAimAssistStrength)||0)/100,0,1);
  }

  combatAimHudState(){
    const target=this.player?.homingAmmoTimer>0?this.homingTarget:null;
    const incoming=this.projectiles.projectiles.find((projectile)=>
      projectile.guided&&projectile.homingTarget===this.player
    );
    if(!target&&!incoming)return {visible:false};
    if(!target){
      return {visible:true,x:this.canvas.clientWidth/2,y:this.canvas.clientHeight*.29,
        locked:true,blocked:true,label:'¡MISIL TELEDIRIGIDO!'};
    }
    const projected=target.car.position.clone();
    projected.y+=(target.car.userData.collisionHeight||.82)*.7;
    projected.project(this.camera);
    const width=this.canvas.clientWidth;
    const height=this.canvas.clientHeight;
    const offscreen=projected.z>1||Math.abs(projected.x)>1||Math.abs(projected.y)>1;
    return {
      visible:true,
      x:THREE.MathUtils.clamp((projected.x*.5+.5)*width,28,width-28),
      y:THREE.MathUtils.clamp((-.5*projected.y+.5)*height,28,height-28),
      locked:true,
      confirmed:true,
      offscreen,
      label:target.displayName||'OBJETIVO',
    };
  }

  onlineRuptureSpectatorVehicle(){
    const session=this.onlineSession;
    const state=session?.matchState;
    if(!session||session.match.mode!=='rupture'||!this.player?.ruptureEliminated||!state)return null;
    const contenders=(state.scoreboard||[]).filter((entry)=>
      entry.id!==session.playerId&&!entry.eliminated&&!entry.disabled
    );
    const retained=contenders.find((entry)=>entry.id===session.spectatorTargetId);
    const selected=retained||contenders[0]||null;
    session.spectatorTargetId=selected?.id||null;
    return selected?this.onlineVehicles.get(selected.id)?.vehicle||null:null;
  }

  updateChaseCamera(dt) {
    const spectatorVehicle=this.onlineRuptureSpectatorVehicle();
    const followedVehicle=spectatorVehicle||this.player;
    const spectating=Boolean(spectatorVehicle);
    const car=followedVehicle.car;
    const focusPosition=tempC.copy(
      this.onlineSession&&!spectating?this.onlineSession.renderPosition:car.position,
    );
    const headingOrientation=this.onlineSession&&!spectating
      ?this.onlineSession.renderRotation
      :car.quaternion;
    const cameraDiscontinuity=(focusPosition.x-this.cameraTarget.x)**2
      +(focusPosition.z-this.cameraTarget.z)**2>64;
    const previousHeading=this.cameraFollowedVehicle===followedVehicle&&!cameraDiscontinuity
      ?this.cameraHeading
      :null;
    stabilizedChaseHeading(
      headingOrientation,
      followedVehicle.yaw,
      previousHeading,
      !followedVehicle.wheelsGrounded,
      tempA,
    );
    this.cameraHeading.copy(tempA);
    this.cameraFollowedVehicle=followedVehicle;
    const forward=this.cameraHeading;
    if(this.input.consumeCameraDistance({allowKeyboard:Boolean(followedVehicle.grounded)})){
      this.cameraDistanceIndex=(this.cameraDistanceIndex+1)%CAMERA_DISTANCE_PRESETS.length;
      this.callbacks.onCameraDistance?.(CAMERA_DISTANCE_PRESETS[this.cameraDistanceIndex]);
    }
    const stickRadius=THREE.MathUtils.clamp(Math.hypot(this.combatLookX,this.combatLookY),0,1);
    // RS o mouse mueven directamente el cañón y la cámara acompaña ese mismo
    // ángulo; ambos comparten exactamente el eje indicado por el láser.
    // `weaponAimDirection` define yaw positivo hacia la derecha del auto,
    // mientras que rotar el forward (-Z) alrededor de +Y con Three.js lo
    // desplaza hacia la izquierda. Invertimos sólo el yaw orbital para que
    // cámara, láser y cañón recorran la pantalla en el mismo sentido.
    const targetOrbitYaw=(this.combatCameraReverse?Math.PI:0)-this.combatCameraAimYaw;
    const orbitBlend=1-Math.exp(-dt*18);
    this.cameraOrbitYaw=normalizeAngle(
      this.cameraOrbitYaw+normalizeAngle(targetOrbitYaw-this.cameraOrbitYaw)*orbitBlend,
    );
    const manualCamera=stickRadius>.001||this.combatCameraReverse||Boolean(this.combatAimTarget)
      ||this.combatPrecisionAim;
    const cameraForward=tempD.copy(forward).applyAxisAngle(CAMERA_UP,this.cameraOrbitYaw).normalize();
    const speedRatio=THREE.MathUtils.clamp(
      Math.abs(followedVehicle.forwardSpeed)/followedVehicle.config.maxSpeed,0,1.4,
    );
    const cameraPreset=CAMERA_DISTANCE_PRESETS[this.cameraDistanceIndex]||CAMERA_DISTANCE_PRESETS[0];
    const distance=cameraPreset.distance
      +speedRatio*cameraPreset.speedDistance
      +Math.min(this.player.airTime,cameraPreset.airDistance);
    const height=cameraPreset.height
      +speedRatio*cameraPreset.speedHeight
      +Math.min(focusPosition.y*.07,.58);
    const desired=tempB.copy(focusPosition).addScaledVector(cameraForward,-distance);
    desired.y+=height;
    const cameraAnchor=focusPosition.clone().add(new THREE.Vector3(0,.45,0));
    this.resolveCameraObstruction(cameraAnchor,desired);
    const verticalSpeed=Number(followedVehicle.velocity?.y)||0;
    const verticalProfile=chaseCameraVerticalProfile(verticalSpeed,!followedVehicle.wheelsGrounded);
    const previousCameraY=this.camera.position.y;
    const positionAlpha=1-Math.exp(-dt*(manualCamera?10:followedVehicle.wheelsGrounded?6.2:3.4));
    this.camera.position.lerp(desired,positionAlpha);
    // El seguimiento horizontal conserva la suavidad arcade, pero la altura
    // debe alcanzar al vehículo con mayor rapidez. De lo contrario, al volar
    // con la trompa hacia arriba el auto abandona el encuadre antes de que la
    // cámara termine de subir.
    const verticalPositionRate=manualCamera
      ?Math.max(10,verticalProfile.positionRate)
      :verticalProfile.positionRate;
    this.camera.position.y=THREE.MathUtils.lerp(
      previousCameraY,
      desired.y,
      1-Math.exp(-dt*verticalPositionRate),
    );
    const forwardLead=THREE.MathUtils.clamp(Math.cos(this.cameraOrbitYaw),0,1);
    const aimDirection=spectating
      ?cameraForward
      :this.combatCameraReverse
        ?tempE.copy(cameraForward).multiplyScalar(-1)
        :chaseCameraAimDirection(cameraForward,this.combatCameraAimPitch,tempE);
    const target=focusPosition.clone()
      .addScaledVector(aimDirection,(1.25+speedRatio*.9)*forwardLead)
      .add(new THREE.Vector3(0,.34+verticalProfile.targetLead,0));
    const previousTargetY=this.cameraTarget.y;
    this.cameraTarget.lerp(target,1-Math.exp(-dt*(manualCamera?11:8)));
    this.cameraTarget.y=THREE.MathUtils.lerp(
      previousTargetY,
      target.y,
      1-Math.exp(-dt*(manualCamera?Math.max(11,verticalProfile.targetRate):verticalProfile.targetRate)),
    );
    const nitroTarget=followedVehicle.boosting?1:0;
    this.nitroCameraBlend=THREE.MathUtils.lerp(this.nitroCameraBlend,nitroTarget,1-Math.exp(-dt*(nitroTarget?10:7)));
    if(this.nitroCameraBlend>.002){
      const vibration=(.014+speedRatio*.014)*this.nitroCameraBlend;
      this.camera.position.x+=Math.sin(this.gameElapsed*71)*vibration;
      this.camera.position.y+=Math.sin(this.gameElapsed*89+1.2)*vibration*.58;
      this.camera.position.z+=Math.sin(this.gameElapsed*63+2.4)*vibration*.72;
    }
    if(this.cameraShake>.001){
      const shake=this.cameraShake*this.cameraShakeStrength;
      this.camera.position.add(new THREE.Vector3((Math.random()-.5)*shake,(Math.random()-.5)*shake,(Math.random()-.5)*shake));
      this.cameraShake*=Math.exp(-9*dt);
    }
    this.camera.lookAt(this.cameraTarget);
    const baseFov=this.combatPrecisionAim?51:58;
    this.camera.fov=THREE.MathUtils.lerp(
      this.camera.fov,followedVehicle.boosting?baseFov+10:baseFov,1-Math.exp(-5*dt),
    );this.camera.updateProjectionMatrix();
    this.callbacks.onAim?.(this.combatAimHudState());
  }

  configureMapLighting() {
    if(!this.world)return;
    const shadowSpan=this.world.shadowSpan??Math.hypot(this.world.width,this.world.depth)*.5+7;
    const shadowCamera=this.sun.shadow.camera;
    shadowCamera.left=-shadowSpan;
    shadowCamera.right=shadowSpan;
    shadowCamera.top=shadowSpan;
    shadowCamera.bottom=-shadowSpan;
    shadowCamera.near=.5;
    shadowCamera.far=170;
    shadowCamera.updateProjectionMatrix();
    this.sun.target.position.set(0,0,0);
    this.sun.position.copy(this.sunOffset);
    this.sun.target.updateMatrixWorld();
    this.sun.shadow.needsUpdate=true;
  }

  resolveCameraObstruction(anchor, desired) {
    const offset=desired.clone().sub(anchor);
    const planarDistance=Math.hypot(offset.x,offset.z);
    if(planarDistance<.001)return desired;
    const direction=new THREE.Vector3(offset.x/planarDistance,0,offset.z/planarDistance);
    let allowedDistance=planarDistance;
    for(const obstacle of this.world.obstacles){
      const distance=rayObstacleDistance(anchor,direction,obstacle,planarDistance,.24);
      if(distance===null)continue;
      const ratio=distance/planarDistance;
      const rayHeight=anchor.y+offset.y*ratio;
      if(rayHeight<obstacle.minY-.12||rayHeight>obstacle.maxY+.12)continue;
      allowedDistance=Math.min(allowedDistance,Math.max(.72,distance-.38));
    }
    if(allowedDistance<planarDistance){
      desired.copy(anchor).addScaledVector(offset,allowedDistance/planarDistance);
    }
    const unclampedX=desired.x;
    const unclampedZ=desired.z;
    desired.x=THREE.MathUtils.clamp(desired.x,-this.world.width/2+.58,this.world.width/2-.58);
    desired.z=THREE.MathUtils.clamp(desired.z,-this.world.depth/2+.58,this.world.depth/2-.58);
    const boundaryCompression=Math.hypot(desired.x-unclampedX,desired.z-unclampedZ);
    if(boundaryCompression>.01)desired.y+=Math.min(2.1,boundaryCompression*.42);
    return desired;
  }

  updateMenu(dt) {
    if(!this.showcaseCar)return;
    this.elapsed+=dt;
    const desired=this.getMenuCameraPosition();
    const spawn=this.world.showcase || this.world.spawn;
    this.camera.position.lerp(desired,1-Math.exp(-2.4*dt));
    this.cameraTarget.lerp(tempA.copy(spawn).add(new THREE.Vector3(0,-.08,0)),1-Math.exp(-3.5*dt));
    this.camera.lookAt(this.cameraTarget);
    this.showcaseCar.userData.visual.position.y=Math.sin(this.elapsed*2)*.018;
    this.showcaseCar.rotation.y=(this.world.showcaseYaw ?? this.world.spawnYaw)-.14+Math.sin(this.elapsed*.45)*.08;
    this.camera.fov=55;this.camera.updateProjectionMatrix();
    this.audio.update(0,false,false);
  }

  getMenuCameraPosition() {
    const spawn=this.world.showcase || this.world.spawn;
    const orbit=this.world.id==='garden'?4.65:4.15;
    const preferredAngle=this.elapsed*.045+2.45;
    let desired=null;
    for(const offset of [0,.55,-.55,1.05,-1.05,Math.PI]){
      const angle=preferredAngle+offset;
      const candidate=new THREE.Vector3(spawn.x+Math.sin(angle)*orbit,spawn.y+1.7,spawn.z+Math.cos(angle)*orbit);
      if(!pointInsideObstacle(candidate,this.world.obstacles,.35)){desired=candidate;break;}
    }
    desired ||= new THREE.Vector3(spawn.x,spawn.y+1.7,spawn.z-orbit);
    return desired;
  }

  snapMenuCamera() {
    if(!this.world)return;
    this.camera.position.copy(this.getMenuCameraPosition());
    this.cameraTarget.copy(this.world.showcase || this.world.spawn).add(new THREE.Vector3(0,-.08,0));
    this.camera.lookAt(this.cameraTarget);
    this.camera.fov=55;this.camera.updateProjectionMatrix();
  }

  renderFrame() {
    // El siguiente frame se agenda antes de actualizar. Así, una excepción
    // aislada queda visible en consola pero no mata para siempre el juego ni
    // deja bloqueados los temporizadores de transición.
    requestAnimationFrame(()=>this.renderFrame());
    const now=performance.now();
    const dt=Math.min(this.clock.getDelta(),.05);
    if(this.mode!=='garage'&&this.mode!=='paused')this.world?.update?.(dt,this.camera);
    if(this.mode==='game')this.updateGame(dt);
    else if(this.mode==='online-game')this.updateOnlineGame(dt);
    else if(this.mode==='post-match')this.updatePostMatch(dt);
    else if(this.mode==='trailer')this.trailerDirector?.update(dt);
    else if(this.mode==='cinematic')this.cinematicStudio?.update(dt);
    else if(this.mode==='menu'||this.mode==='online')this.updateMenu(dt);
    else if(this.mode==='garage'){
      this.garagePreview.update(dt);
      const previewCannon=getCannon(this.garagePreview.loadout.cannonId);
      this.audio.prepareCannonSample(previewCannon);
      const previewProjectile=getProjectile(this.garagePreview.loadout.projectileId);
      this.audio.prepareDesignSample(previewProjectile,'travel');
      this.audio.prepareDesignSample(previewProjectile,'impact');
      this.audio.prepareNitroSample(this.garagePreview.car?.userData.nitroEffect);
      this.audio.prepareDesignSample(
        getDeathExplosion(this.garagePreview.loadout.explosionId),
        'sound',
      );
      this.audio.prepareDesignSample(
        getTombstone(this.garagePreview.loadout.tombstoneId),
        'sound',
      );
      const previewEngine=this.garagePreview.car?.userData.engineSound;
      const engineActive=this.garagePreview.slot==='engineSoundId'&&Boolean(previewEngine);
      this.audio.update(engineActive?9+Math.sin(this.garagePreview.time*1.8)*3:0,false,engineActive,0,null,previewEngine);
      const shot=this.garagePreview.consumeShotSound();
      if(shot){
        this.audio.cannonFire(shot.cannon,.72);
        this.audio.projectileTravel(shot.projectile,.58);
      }
    }
    else if(this.mode!=='trailer'&&this.mode!=='cinematic')this.audio.update(0,false,false);
    if(!this.shouldRenderFrame(now))return;
    if(this.mode==='garage')this.renderGaragePreview();
    else{
      this.renderer.setScissorTest(false);
      const renderSize=this.mode==='trailer'
        ?this.trailerDirector?.getRenderSize?.()||{width:window.innerWidth,height:window.innerHeight}
        :this.mode==='cinematic'
          ?this.cinematicStudio?.getRenderSize?.()||{width:window.innerWidth,height:window.innerHeight}
          :{width:window.innerWidth,height:window.innerHeight};
      this.renderer.setViewport(0,0,renderSize.width,renderSize.height);
      this.camera.aspect=renderSize.width/renderSize.height;
      this.camera.updateProjectionMatrix();
      this.renderer.render(this.scene,this.camera);
      if(this.mode==='cinematic')this.cinematicStudio?.afterRender?.();
    }
    this.recordRenderedFrame(now);
  }

  renderGaragePreview(){
    const rect=this.garagePreviewElement?.getBoundingClientRect();
    if(!rect||rect.width<2||rect.height<2)return;
    this.renderer.setScissorTest(false);
    this.renderer.setViewport(0,0,window.innerWidth,window.innerHeight);
    this.renderer.setClearColor('#131122',1);
    this.renderer.clear(true,true,true);
    const bottom=window.innerHeight-rect.bottom;
    this.renderer.setViewport(rect.left,bottom,rect.width,rect.height);
    this.renderer.setScissor(rect.left,bottom,rect.width,rect.height);
    this.renderer.setScissorTest(true);
    this.camera.aspect=rect.width/rect.height;
    this.camera.updateProjectionMatrix();
    this.renderer.render(this.garagePreview.scene,this.camera);
    this.renderer.setScissorTest(false);
  }

  resize() {
    if(this.mode==='cinematic'&&this.cinematicStudio?.handleResize?.())return;
    this.camera.aspect=window.innerWidth/window.innerHeight;this.camera.updateProjectionMatrix();
    this.renderer.setPixelRatio(this.graphicsPixelRatio());
    this.renderer.setSize(window.innerWidth,window.innerHeight,false);
  }
}
